import { isAxiosError, type AxiosResponse } from 'axios';
import api from '../axios';
import {
  getCommerceCatalogMaterials,
  unwrapCommerceCatalogMaterialsPage,
} from './commerceService.ts';
import {
  extractCommerceMaterialsDataArray,
  parseCatalogMaterialItemsToParsedCategories,
  parseCommerceMaterialsPayload,
  type ParsedCategory,
} from '../components/Perfil/PerfilMain/MisMaterialesComercio/commerceMaterialsModel.ts';
import { coerceRawUnitToMaterialSelectValue } from '../utils/marketplace/materialUnitSelectOptions.ts';

export const priceListFileAccept = [
  'application/pdf',
  '.pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
  '.jpg',
  '.jpeg',
  '.png',
  '.webp',
  'text/plain',
  '.txt',
  'application/json',
  '.json',
  'text/csv',
  'application/csv',
  '.csv',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  '.xls',
  '.xlsx',
].join(',');

export const excelAccept = priceListFileAccept;

const SHEET_PREVIEW_POLL_MAX_MS = 600000;
const SHEET_PREVIEW_POLL_CATALOG_PHASE_MAX_MS = 1200000;
const SHEET_PREVIEW_CATALOG_STALL_MS = 600000;
const SHEET_PREVIEW_POLL_INTERVAL_MS = 2000;
const SHEET_PREVIEW_JOB_ROWS_PAGE_SIZE = 150;
const COMMERCE_CATALOG_MATERIALS_PAGE_SIZE = 100;
const SHEET_CONFIRM_POLL_MAX_MS = 1_200_000;
const SHEET_CONFIRM_POLL_START_MS = 500;
const SHEET_CONFIRM_POLL_CAP_MS = 3_000;

export type SheetPreviewDocumentAssessment = {
  titleForUser?: string;
  bodyForUser?: string;
  contentSummary?: string;
  irrelevanceReason?: string;
};

export type SheetPreviewSummary = {
  total?: number;
  matched?: number;
  unresolved?: number;
  readyToConfirm?: number;
};

export type SheetPreviewPayload = {
  status?: string;
  rows: unknown[];
  documentAssessment?: SheetPreviewDocumentAssessment | null;
  extraction?: unknown;
  summary?: SheetPreviewSummary | null;
};

export type SheetRowCompleteness = {
  canIncludeInConfirm?: boolean;
  missingRequired?: ('name' | 'categoryId' | 'unit')[];
  reasonCodes?: string[];
};

export type SheetPreviewJobPollUpdate = {
  loaderPhase: string;
  phase: string;
  detailPhase?: string | null;
  ingestionProgressLabel?: string | null;
};

export type SheetPreviewCatalogMatchRowsProgress = {
  processed: number;
  total: number;
};

export type SheetPreviewIngestionProgress = {
  totalUnits?: number;
  nextUnitIndex?: number;
  accumulatedRowCount?: number;
  activeDocumentUnits?: number[] | null;
  catalogMatchRows?: SheetPreviewCatalogMatchRowsProgress | null;
  extraction?: unknown;
};

function readFiniteNumber(v: unknown): number | undefined {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() && Number.isFinite(Number(v))) return Number(v);
  return undefined;
}

function parseIngestionProgress(raw: unknown): SheetPreviewIngestionProgress | null {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const totalUnits = readFiniteNumber(o.totalUnits);
  const nextUnitIndex = readFiniteNumber(o.nextUnitIndex);
  const accumulatedRowCount = readFiniteNumber(o.accumulatedRowCount);
  let activeDocumentUnits: number[] | null | undefined;
  const adu = o.activeDocumentUnits;
  if (adu === null) {
    activeDocumentUnits = null;
  } else if (Array.isArray(adu)) {
    activeDocumentUnits = adu.filter((x): x is number => typeof x === 'number' && Number.isFinite(x));
  }
  let catalogMatchRows: SheetPreviewCatalogMatchRowsProgress | null | undefined;
  const cm = o.catalogMatchRows;
  if (cm === null) {
    catalogMatchRows = null;
  } else if (cm != null && typeof cm === 'object' && !Array.isArray(cm)) {
    const cmo = cm as Record<string, unknown>;
    const processed = readFiniteNumber(cmo.processed);
    const total = readFiniteNumber(cmo.total);
    if (processed !== undefined || total !== undefined) {
      catalogMatchRows = {
        processed: processed ?? 0,
        total: total ?? 0,
      };
    }
  }
  const out: SheetPreviewIngestionProgress = {};
  if (totalUnits !== undefined) out.totalUnits = totalUnits;
  if (nextUnitIndex !== undefined) out.nextUnitIndex = nextUnitIndex;
  if (accumulatedRowCount !== undefined) out.accumulatedRowCount = accumulatedRowCount;
  if (activeDocumentUnits !== undefined) out.activeDocumentUnits = activeDocumentUnits;
  if (catalogMatchRows !== undefined) out.catalogMatchRows = catalogMatchRows;
  if (o.extraction !== undefined) out.extraction = o.extraction;
  if (
    out.totalUnits === undefined &&
    out.nextUnitIndex === undefined &&
    out.accumulatedRowCount === undefined &&
    out.activeDocumentUnits === undefined &&
    out.catalogMatchRows === undefined
  ) {
    return null;
  }
  return out;
}

export function formatSheetPreviewIngestionProgressForLoader(
  ingestion: SheetPreviewIngestionProgress | null | undefined,
  detailPhase: string | null | undefined
): string | null {
  if (ingestion == null) return null;
  const dp = (detailPhase ?? '').trim().toLowerCase();
  const cm = ingestion.catalogMatchRows;
  if (dp === 'catalog_match' && cm != null && typeof cm.total === 'number' && cm.total > 0) {
    return `Clasificando en catálogo: ${cm.processed} / ${cm.total}`;
  }
  const active = ingestion.activeDocumentUnits;
  if (Array.isArray(active) && active.length > 0) {
    const lo = Math.min(...active);
    const hi = Math.max(...active);
    const tu = ingestion.totalUnits;
    const a = lo + 1;
    const b = hi + 1;
    if (a === b) {
      return tu != null && tu > 0 ? `Analizando página ${a} de ${tu}` : `Analizando página ${a}`;
    }
    return tu != null && tu > 0 ? `Analizando páginas ${a}–${b} de ${tu}` : `Analizando páginas ${a}–${b}`;
  }
  const nu = ingestion.nextUnitIndex;
  const tu = ingestion.totalUnits;
  if (tu != null && tu > 0 && nu != null && nu >= 0) {
    const processed = Math.min(nu, tu);
    return `Páginas procesadas: ${processed} de ${tu}`;
  }
  const arc = ingestion.accumulatedRowCount;
  if (arc != null && arc > 0 && (dp === 'extracting' || dp === 'document_screen')) {
    return `${arc} filas detectadas hasta ahora`;
  }
  return null;
}

function unwrapPreviewData(resBody: unknown): Record<string, unknown> | null {
  if (resBody == null || typeof resBody !== 'object') return null;
  const o = resBody as Record<string, unknown>;
  const inner = o.data;
  if (inner && typeof inner === 'object') {
    return inner as Record<string, unknown>;
  }
  return o;
}

function parseSheetPreviewSummary(raw: unknown): SheetPreviewSummary | null {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const s = raw as Record<string, unknown>;
  const num = (k: string): number | undefined => {
    const v = s[k];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string' && v.trim() && Number.isFinite(Number(v))) return Number(v);
    return undefined;
  };
  const total = num('total');
  const matched = num('matched');
  const unresolved = num('unresolved');
  const readyToConfirm = num('readyToConfirm');
  if (
    total === undefined &&
    matched === undefined &&
    unresolved === undefined &&
    readyToConfirm === undefined
  ) {
    return null;
  }
  return { total, matched, unresolved, readyToConfirm };
}

export function getSheetPreviewPayload(resBody: unknown): SheetPreviewPayload | null {
  const d = unwrapPreviewData(resBody);
  if (!d) return null;
  const status = typeof d.status === 'string' ? d.status : undefined;
  const rows = Array.isArray(d.rows) ? d.rows : [];
  const da = d.documentAssessment;
  const documentAssessment =
    da && typeof da === 'object' ? (da as SheetPreviewDocumentAssessment) : null;
  const extraction = d.extraction !== undefined ? d.extraction : undefined;
  const sum = d.summary;
  const summary =
    sum != null && typeof sum === 'object' && !Array.isArray(sum) ? parseSheetPreviewSummary(sum) : null;
  return { status, rows, documentAssessment, extraction, summary };
}

export function readSheetRowCompleteness(row: unknown): SheetRowCompleteness | null {
  if (row == null || typeof row !== 'object' || Array.isArray(row)) return null;
  const c = (row as Record<string, unknown>).completeness;
  if (c == null || typeof c !== 'object' || Array.isArray(c)) return null;
  const o = c as Record<string, unknown>;
  const missingRaw = o.missingRequired;
  const missingRequired = Array.isArray(missingRaw)
    ? (missingRaw.filter((x) => x === 'name' || x === 'categoryId' || x === 'unit') as (
        | 'name'
        | 'categoryId'
        | 'unit'
      )[])
    : undefined;
  const rcRaw = o.reasonCodes;
  const reasonCodes = Array.isArray(rcRaw)
    ? rcRaw
        .filter((x): x is string => typeof x === 'string')
        .map((x) => x.trim())
        .filter((s) => s.length > 0)
    : undefined;
  return {
    canIncludeInConfirm: typeof o.canIncludeInConfirm === 'boolean' ? o.canIncludeInConfirm : undefined,
    missingRequired,
    reasonCodes,
  };
}

export function previewRowsMissingCompletenessBlock(previewRows: unknown[]): boolean {
  return previewRows.some((row) => readSheetRowCompleteness(row) == null);
}

const SHEET_REASON_CODE_HINTS: Record<string, string> = {
  MISSING_NAME: 'Falta nombre',
  MISSING_CATEGORY: 'Falta categoría',
  MISSING_UNIT: 'Falta unidad',
  NO_CATALOG_MATCH: 'Revisá categoría y nombre',
  LOW_CONFIDENCE_CATEGORY: 'Categoría con baja confianza',
  LOW_CONFIDENCE_MATERIAL: 'Nombre sugerido con baja confianza',
  CATALOG_NEAR_DEDUP_ADJUSTED: 'Se ajustó entre materiales parecidos',
  UNIT_NOT_IN_SYSTEM: 'Unidad no reconocida en el sistema',
};

export function readSheetRowReasonCodesHint(row: unknown): string {
  const c = readSheetRowCompleteness(row);
  if (c?.reasonCodes == null || c.reasonCodes.length === 0) return '';
  return c.reasonCodes.map((code) => SHEET_REASON_CODE_HINTS[code] ?? code).join(' · ');
}

export function rowUnitRequiresKnownSelectValue(row: unknown, eff: unknown): boolean {
  const display = readSheetRowUnitDisplay(eff).trim();
  const coerced = coerceRawUnitToMaterialSelectValue(readSheetRowUnitDisplay(eff));
  const codes = readSheetRowCompleteness(row)?.reasonCodes;
  const unitNotInSystem = codes != null && codes.includes('UNIT_NOT_IN_SYSTEM');
  if (coerced !== '') return false;
  if (display.length > 0) return true;
  if (unitNotInSystem) return true;
  return false;
}

type SheetRematchPickLike = {
  userCategoryId?: number | '';
  rawName?: string;
  createCatalogMaterial?: boolean;
  rejectSuggestedCatalogMatch?: boolean;
};

export function resolveSheetConfirmUserCategoryId(
  row: unknown,
  pick: SheetRematchPickLike | undefined
): number | null {
  const fromPick = pick != null && pick.userCategoryId !== '' ? Number(pick.userCategoryId) : NaN;
  if (Number.isFinite(fromPick) && fromPick > 0) return fromPick;
  const resolved = readSheetRowResolvedUserCategoryId(row);
  if (resolved != null && resolved > 0) return resolved;
  return null;
}

function readSheetImportedRawNameOnly(row: unknown): string {
  if (row == null || typeof row !== 'object' || Array.isArray(row)) return '';
  const o = row as Record<string, unknown>;
  const src = o.source;
  if (src != null && typeof src === 'object' && !Array.isArray(src)) {
    const rn = (src as Record<string, unknown>).rawName;
    if (typeof rn === 'string' && rn.trim()) return rn.trim();
  }
  const norm = o.normalized;
  if (norm != null && typeof norm === 'object' && !Array.isArray(norm)) {
    const n = (norm as Record<string, unknown>).name;
    if (typeof n === 'string' && n.trim()) return n.trim();
  }
  return '';
}

export function getSheetRowDefaultRawName(row: unknown): string {
  if (row == null || typeof row !== 'object' || Array.isArray(row)) return '';
  const o = row as Record<string, unknown>;
  if (Object.prototype.hasOwnProperty.call(o, 'rawName') && typeof o.rawName === 'string') {
    return o.rawName.trim();
  }
  return readSheetImportedRawNameOnly(row);
}

export function resolveSheetRematchDisplayedRawName(row: unknown, pick: SheetRematchPickLike | undefined): string {
  if (pick != null) {
    return String(pick.rawName ?? '').trim();
  }
  return getSheetRowDefaultRawName(row).trim();
}

export function readSheetRowSuggestedCatalogMaterialName(row: unknown): string {
  if (row == null || typeof row !== 'object' || Array.isArray(row)) return '';
  const o = row as Record<string, unknown>;
  const m = o.match;
  if (m != null && typeof m === 'object' && !Array.isArray(m)) {
    const nm = readTrimmedString((m as Record<string, unknown>).materialName);
    if (nm) return nm;
  }
  return readTrimmedString(o.materialName) ?? '';
}

export function rowMeetsSheetConfirmMinimums(
  row: unknown,
  i: number,
  eff: unknown,
  rematchByRowIndex: SheetRematchByRowIndex
): boolean {
  if (rowUsesCatalogDirectConfirm(row)) {
    const pick = rematchByRowIndex[i];
    if (pick?.rejectSuggestedCatalogMatch === true && pick?.createCatalogMaterial !== true) {
      return false;
    }
    const rejectCreate =
      pick?.rejectSuggestedCatalogMatch === true && pick?.createCatalogMaterial === true;
    const catId = resolveSheetConfirmUserCategoryId(eff, pick);
    const catOk = catId != null;
    const nameOk = getSheetRowDefaultRawName(eff).trim().length > 0;
    if (rejectCreate) {
      const unitCanon = coerceRawUnitToMaterialSelectValue(readSheetRowUnitDisplay(eff));
      return catOk && nameOk && unitCanon !== '';
    }
    if (rowUnitRequiresKnownSelectValue(row, eff)) return false;
    return catOk && nameOk;
  }
  if (!rowNeedsCategoryRematch(row)) {
    const nameOk = getSheetRowDefaultRawName(eff).trim().length > 0;
    if (!nameOk) return false;
    if (rowUnitRequiresKnownSelectValue(row, eff)) return false;
    return true;
  }
  const pick = rematchByRowIndex[i];
  const catOk = resolveSheetConfirmUserCategoryId(eff, pick) != null;
  const rawOk = resolveSheetRematchDisplayedRawName(eff, pick).length > 0;
  const unitDisplay = readSheetRowUnitDisplay(eff).trim();
  const wantsCatalogCreate = pick?.createCatalogMaterial === true;
  const unitOk = wantsCatalogCreate
    ? coerceRawUnitToMaterialSelectValue(readSheetRowUnitDisplay(eff)) !== ''
    : unitDisplay.length > 0 && !rowUnitRequiresKnownSelectValue(row, eff);
  return catOk && rawOk && unitOk;
}

function numericMaterialIdFromUnknown(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim()) {
    const n = Number(v.trim());
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

export function rowHasPersistableMaterialId(row: unknown): boolean {
  if (row == null || typeof row !== 'object') return false;
  const o = row as Record<string, unknown>;
  if (numericMaterialIdFromUnknown(o.materialId) != null) return true;
  const m = o.match;
  if (m && typeof m === 'object') {
    return numericMaterialIdFromUnknown((m as Record<string, unknown>).materialId) != null;
  }
  return false;
}

export function readSheetRowListingMode(row: unknown): 'catalog_linked' | 'commerce_listing' | null {
  if (row == null || typeof row !== 'object' || Array.isArray(row)) return null;
  const o = row as Record<string, unknown>;
  const m = o.match;
  if (m != null && typeof m === 'object' && !Array.isArray(m)) {
    const lm = (m as Record<string, unknown>).listingMode;
    if (lm === 'catalog_linked' || lm === 'commerce_listing') return lm;
  }
  const root = o.listingMode;
  if (root === 'catalog_linked' || root === 'commerce_listing') return root;
  return null;
}

export function rowUsesCatalogDirectConfirm(row: unknown): boolean {
  return rowHasPersistableMaterialId(row) && readSheetRowListingMode(row) !== 'commerce_listing';
}

export function filterSheetRowsForConfirm(rows: unknown[]): unknown[] {
  return rows.filter(rowHasPersistableMaterialId);
}

export type SheetCategoryOption = { id: number; name: string };

export type SheetRematchPick = {
  userCategoryId: number | '';
  rawName: string;
  createCatalogMaterial?: boolean;
  rejectSuggestedCatalogMatch?: boolean;
};

export type SheetRematchByRowIndex = Record<number, SheetRematchPick>;

export async function fetchMaterialSheetCategories(): Promise<SheetCategoryOption[]> {
  const res = await api.get('/category/all', getAuthHeaders());
  const raw = res.data as unknown;
  const arr = Array.isArray(raw)
    ? raw
    : raw != null &&
        typeof raw === 'object' &&
        !Array.isArray(raw) &&
        Array.isArray((raw as Record<string, unknown>).data)
      ? ((raw as Record<string, unknown>).data as unknown[])
      : [];
  const out: SheetCategoryOption[] = [];
  for (const item of arr) {
    if (item == null || typeof item !== 'object' || Array.isArray(item)) continue;
    const o = item as Record<string, unknown>;
    const id = Number(o.id);
    const name = typeof o.name === 'string' ? o.name.trim() : '';
    if (Number.isFinite(id) && name.length > 0) out.push({ id, name });
  }
  return out.sort((a, b) => a.name.localeCompare(b.name, 'es'));
}

function readTrimmedString(v: unknown): string | null {
  if (typeof v !== 'string' || !v.trim()) return null;
  return v.trim();
}

function numericCategoryIdFromUnknown(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v) && v > 0) return v;
  if (typeof v === 'string' && v.trim()) {
    const n = Number(v.trim());
    return Number.isFinite(n) && n > 0 ? n : null;
  }
  return null;
}

export function getSheetRowCategoryDisplay(row: unknown, categoryOptions: SheetCategoryOption[]): string {
  if (row == null || typeof row !== 'object' || Array.isArray(row)) return '—';
  const o = row as Record<string, unknown>;
  const m = o.match;
  const matchObj = m != null && typeof m === 'object' && !Array.isArray(m) ? (m as Record<string, unknown>) : null;
  const strCandidates: unknown[] = [
    o.categoryName,
    o.inferredCategoryName,
    o.userCategoryName,
    matchObj?.categoryName,
    matchObj?.inferredCategoryName,
    matchObj?.userCategoryName,
    matchObj?.label,
    matchObj?.categoryLabel,
  ];
  for (const c of strCandidates) {
    const s = readTrimmedString(c);
    if (s) return s;
  }
  const cat = o.category;
  if (cat != null && typeof cat === 'object' && !Array.isArray(cat)) {
    const nm = readTrimmedString((cat as Record<string, unknown>).name);
    if (nm) return nm;
  }
  if (matchObj?.category != null && typeof matchObj.category === 'object' && !Array.isArray(matchObj.category)) {
    const nm = readTrimmedString((matchObj.category as Record<string, unknown>).name);
    if (nm) return nm;
  }
  const idCandidates = [
    numericCategoryIdFromUnknown(o.userCategoryId),
    numericCategoryIdFromUnknown(o.inferredCategoryId),
    numericCategoryIdFromUnknown(o.categoryId),
    matchObj ? numericCategoryIdFromUnknown(matchObj.categoryId) : null,
    matchObj ? numericCategoryIdFromUnknown(matchObj.userCategoryId) : null,
    matchObj ? numericCategoryIdFromUnknown(matchObj.inferredCategoryId) : null,
  ];
  for (const id of idCandidates) {
    if (id != null) {
      const name = categoryOptions.find((x) => x.id === id)?.name;
      if (name) return name;
      return `Categoría #${id}`;
    }
  }
  return '—';
}

export function readSheetRowResolvedUserCategoryId(row: unknown): number | null {
  if (row == null || typeof row !== 'object' || Array.isArray(row)) return null;
  const o = row as Record<string, unknown>;
  const m = o.match;
  const matchObj = m != null && typeof m === 'object' && !Array.isArray(m) ? (m as Record<string, unknown>) : null;
  const idCandidates = [
    numericCategoryIdFromUnknown(o.userCategoryId),
    numericCategoryIdFromUnknown(o.inferredCategoryId),
    numericCategoryIdFromUnknown(o.categoryId),
    matchObj ? numericCategoryIdFromUnknown(matchObj.categoryId) : null,
    matchObj ? numericCategoryIdFromUnknown(matchObj.userCategoryId) : null,
    matchObj ? numericCategoryIdFromUnknown(matchObj.inferredCategoryId) : null,
  ];
  for (const id of idCandidates) {
    if (id != null && id > 0) return id;
  }
  if (o.category != null && typeof o.category === 'object' && !Array.isArray(o.category)) {
    const cid = numericCategoryIdFromUnknown((o.category as Record<string, unknown>).id);
    if (cid != null && cid > 0) return cid;
  }
  if (matchObj?.category != null && typeof matchObj.category === 'object' && !Array.isArray(matchObj.category)) {
    const cid = numericCategoryIdFromUnknown((matchObj.category as Record<string, unknown>).id);
    if (cid != null && cid > 0) return cid;
  }
  return null;
}

export function readSheetRowPriceValue(row: unknown): number | null {
  if (row == null || typeof row !== 'object' || Array.isArray(row)) return null;
  const o = row as Record<string, unknown>;
  const matchObj =
    o.match != null && typeof o.match === 'object' && !Array.isArray(o.match)
      ? (o.match as Record<string, unknown>)
      : null;
  const tryNum = (v: unknown): number | null => {
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string' && v.trim()) {
      const n = Number(v.trim().replace(/\./g, '').replace(',', '.'));
      return Number.isFinite(n) ? n : null;
    }
    return null;
  };
  const p = tryNum(o.price);
  if (p != null) return p;
  const src = o.source;
  if (src != null && typeof src === 'object' && !Array.isArray(src)) {
    const rp = tryNum((src as Record<string, unknown>).rawPrice);
    if (rp != null) return rp;
  }
  if (matchObj) {
    const mp = tryNum(matchObj.unitPrice);
    if (mp != null) return mp;
  }
  return null;
}

export function readSheetRowUnitDisplay(row: unknown): string {
  if (row == null || typeof row !== 'object' || Array.isArray(row)) return '';
  const o = row as Record<string, unknown>;
  const src = o.source;
  if (src != null && typeof src === 'object' && !Array.isArray(src)) {
    const sr = src as Record<string, unknown>;
    const u = readTrimmedString(sr.rawUnit) ?? readTrimmedString(sr.unit);
    if (u) return u;
  }
  const m = o.match;
  if (m != null && typeof m === 'object' && !Array.isArray(m)) {
    const mr = m as Record<string, unknown>;
    const u = readTrimmedString(mr.unit);
    if (u) return u;
    const mat = mr.material;
    if (mat != null && typeof mat === 'object' && !Array.isArray(mat)) {
      const mu = readTrimmedString((mat as Record<string, unknown>).unit);
      if (mu) return mu;
    }
  }
  return readTrimmedString(o.unit) ?? '';
}

export function readSheetRowAmountDisplay(row: unknown): string {
  if (row == null || typeof row !== 'object' || Array.isArray(row)) return '';
  const o = row as Record<string, unknown>;
  const src = o.source;
  if (src != null && typeof src === 'object' && !Array.isArray(src)) {
    const a = (src as Record<string, unknown>).rawAmount;
    if (typeof a === 'number' && Number.isFinite(a)) return String(a);
    const s = readTrimmedString(a);
    if (s) return s;
  }
  const m = o.match;
  if (m != null && typeof m === 'object' && !Array.isArray(m)) {
    const a = (m as Record<string, unknown>).amount;
    if (typeof a === 'number' && Number.isFinite(a)) return String(a);
    const s = readTrimmedString(a);
    if (s) return s;
  }
  if (typeof o.amount === 'number' && Number.isFinite(o.amount)) return String(o.amount);
  return readTrimmedString(o.amount) ?? '';
}

export function formatSheetRowPriceDisplay(row: unknown): string {
  const n = readSheetRowPriceValue(row);
  if (n == null) return '—';
  try {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(n);
  } catch {
    return String(n);
  }
}

function isCatalogLinkingNoiseForUser(text: string): boolean {
  const t = text.toLowerCase();
  if (t.includes('catálogo global') || t.includes('catalogo global')) return true;
  if (t.includes('vínculo al catálogo') || t.includes('vinculo al catalogo')) return true;
  if (t.includes('listado propio') && (t.includes('catálogo') || t.includes('catalogo'))) return true;
  if ((t.includes('sin vínculo') || t.includes('sin vinculo')) && (t.includes('catálogo') || t.includes('catalogo'))) {
    return true;
  }
  if (
    (t.includes('ítem de tu lista') || t.includes('item de tu lista')) &&
    (t.includes('catálogo') || t.includes('catalogo'))
  ) {
    return true;
  }
  if (t.includes('material sugerido ajustado') && t.includes('parecidos')) return true;
  return false;
}

function isUnitMatchingInstructionNoise(text: string): boolean {
  const t = text.toLowerCase();
  if (t.includes('unidad informada')) return true;
  if (t.includes('unidades del sistema')) return true;
  if (t.includes('elegí') && t.includes('válida') && t.includes('unidad')) return true;
  if (t.includes('elegi') && t.includes('valida') && t.includes('unidad')) return true;
  return false;
}

export function readSheetRowWarningsText(row: unknown): string {
  if (row == null || typeof row !== 'object' || Array.isArray(row)) return '';
  const w = (row as Record<string, unknown>).warnings;
  if (!Array.isArray(w) || w.length === 0) return '';
  const parts: string[] = [];
  for (const item of w) {
    let s: string | null = null;
    if (typeof item === 'string' && item.trim()) s = item.trim();
    else if (item != null && typeof item === 'object' && !Array.isArray(item)) {
      const o = item as Record<string, unknown>;
      s = readTrimmedString(o.message ?? o.text ?? o.code);
    }
    if (s && !isCatalogLinkingNoiseForUser(s) && !isUnitMatchingInstructionNoise(s)) parts.push(s);
  }
  return parts.join(' · ');
}

export type SheetRowFieldPatch = Partial<{
  rawName: string;
  price: string;
  unit: string;
  amount: string;
  unquoted: boolean;
  valueAddedTax: boolean;
  description: string;
  promotion: string;
  promotionDiscount: string;
}>;

export type SheetManualAddRowPayload = {
  userCategoryId: number;
  rawName: string;
  initialPatch: Partial<SheetRowFieldPatch>;
};

export function remapKeyedStateAfterRowRemove<T>(
  map: Record<number, T> | undefined,
  removedIndex: number
): Record<number, T> {
  if (map == null) return {};
  const next: Record<number, T> = {};
  for (const key of Object.keys(map)) {
    const k = Number(key);
    if (!Number.isFinite(k)) continue;
    if (k === removedIndex) continue;
    const nk = k > removedIndex ? k - 1 : k;
    next[nk] = map[k] as T;
  }
  return next;
}

export function parseSheetPriceInputToNumber(raw: string): number | null {
  let t = raw.trim().replace(/\u00a0/g, ' ').replace(/\s/g, '');
  t = t.replace(/US\$/gi, '').replace(/ARS/gi, '').replace(/USD/gi, '').replace(/\$/g, '');
  if (!t) return null;
  const normalized = t.includes(',') && !t.includes('.') ? t.replace(',', '.') : t.replace(/\./g, '').replace(',', '.');
  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
}

export function formatSheetRowPriceInputValue(n: number): string {
  try {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(n);
  } catch {
    return String(n);
  }
}

const SHEET_PROMOTION_DISCOUNT_MAX_PERCENT = 99;

function clampSheetPromotionDiscountPercent(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.min(SHEET_PROMOTION_DISCOUNT_MAX_PERCENT, Math.max(0, n));
}

export function parseSheetPromotionDiscountInputToNumber(raw: string): number | null {
  const t = raw.trim().replace(/\u00a0/g, ' ').replace(/\s/g, '').replace(/%/g, '');
  if (!t) return null;
  const normalized = t.includes(',') && !t.includes('.') ? t.replace(',', '.') : t.replace(/\./g, '').replace(',', '.');
  const n = Number(normalized);
  if (!Number.isFinite(n)) return null;
  return clampSheetPromotionDiscountPercent(n);
}

export function formatSheetRowPromotionDiscountInputValue(n: number): string {
  try {
    const c = clampSheetPromotionDiscountPercent(n);
    const s = new Intl.NumberFormat('es-AR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(c);
    return `${s}\u00a0%`;
  } catch {
    return `${String(clampSheetPromotionDiscountPercent(n))}\u00a0%`;
  }
}

export function readSheetRowPromotionDiscountValue(row: unknown): number | null {
  if (row == null || typeof row !== 'object' || Array.isArray(row)) return null;
  const base = row as Record<string, unknown>;
  if (!Object.prototype.hasOwnProperty.call(base, 'promotionDiscount')) return null;
  if (typeof base.promotionDiscount === 'number' && Number.isFinite(base.promotionDiscount)) {
    return clampSheetPromotionDiscountPercent(base.promotionDiscount);
  }
  if (typeof base.promotionDiscount === 'string' && base.promotionDiscount.trim()) {
    return parseSheetPromotionDiscountInputToNumber(base.promotionDiscount);
  }
  return null;
}

export function applySheetRowFieldPatches(row: unknown, patch: SheetRowFieldPatch | undefined): unknown {
  if (row == null || typeof row !== 'object' || Array.isArray(row)) return row;
  if (patch == null) return row;
  const hasText = (s: string | undefined): boolean => s != null && String(s).trim().length > 0;
  const rawNameTouched = patch.rawName !== undefined;
  const offerTouched =
    patch.unquoted !== undefined ||
    patch.valueAddedTax !== undefined ||
    patch.description !== undefined ||
    patch.promotion !== undefined ||
    patch.promotionDiscount !== undefined;
  if (!rawNameTouched && !hasText(patch.price) && !hasText(patch.unit) && !hasText(patch.amount) && !offerTouched) {
    return row;
  }
  const base = row as Record<string, unknown>;
  const next: Record<string, unknown> = { ...base };
  const prevSrc =
    base.source != null && typeof base.source === 'object' && !Array.isArray(base.source)
      ? ({ ...(base.source as Record<string, unknown>) } as Record<string, unknown>)
      : ({} as Record<string, unknown>);
  let srcDirty = false;
  if (rawNameTouched) next.rawName = String(patch.rawName ?? '').trim();
  if (patch.unquoted !== undefined) {
    next.unquoted = Boolean(patch.unquoted);
    if (patch.unquoted && patch.valueAddedTax === undefined) {
      next.valueAddedTax = false;
    }
  }
  if (patch.valueAddedTax !== undefined) {
    next.valueAddedTax = Boolean(patch.valueAddedTax);
  }
  if (patch.description !== undefined) next.description = String(patch.description ?? '').trim();
  if (patch.promotion !== undefined) next.promotion = String(patch.promotion ?? '').trim();
  if (patch.promotionDiscount !== undefined) {
    if (hasText(patch.promotionDiscount)) {
      const n = parseSheetPromotionDiscountInputToNumber(String(patch.promotionDiscount));
      if (n != null) next.promotionDiscount = n;
    } else {
      delete next.promotionDiscount;
    }
  }
  if (hasText(patch.price)) {
    const n = parseSheetPriceInputToNumber(String(patch.price));
    if (n != null) {
      next.price = n;
      prevSrc.rawPrice = n;
      srcDirty = true;
    }
  }
  if (hasText(patch.unit)) {
    prevSrc.rawUnit = String(patch.unit).trim();
    srcDirty = true;
  }
  if (hasText(patch.amount)) {
    const rawAmt = String(patch.amount).trim();
    const num = Number(rawAmt.replace(',', '.'));
    prevSrc.rawAmount = Number.isFinite(num) ? num : rawAmt;
    srcDirty = true;
  }
  if (srcDirty) next.source = prevSrc;
  return next;
}

export function mergePreviewRowsWithFieldPatches(
  previewRows: unknown[],
  patches: Record<number, SheetRowFieldPatch> | undefined
): unknown[] {
  if (patches == null || Object.keys(patches).length === 0) return previewRows;
  return previewRows.map((r, i) => applySheetRowFieldPatches(r, patches[i]));
}

export const SHEET_REVIEW_EXCLUDED_DRAFT_KEY = 'sheetReviewExcluded';

export function nextSheetRowIndexForAppend(previewRows: unknown[]): number {
  let max = 0;
  previewRows.forEach((row, i) => {
    if (row != null && typeof row === 'object' && !Array.isArray(row)) {
      const n = Number((row as Record<string, unknown>).rowIndex);
      if (Number.isFinite(n)) max = Math.max(max, n);
      else max = Math.max(max, i + 1);
    } else {
      max = Math.max(max, i + 1);
    }
  });
  return max + 1;
}

export function createManualSheetReviewRow(rowIndex: number): Record<string, unknown> {
  return {
    rowIndex,
    rawName: '',
    source: { rawName: '' },
    match: { materialId: null, listingMode: 'commerce_listing' },
    sheetReviewManual: true,
  };
}

export function mergePreviewRowsForDraftSave(
  previewRows: unknown[],
  patches: Record<number, SheetRowFieldPatch> | undefined,
  excludedByRowIndex: Record<number, boolean> | undefined
): unknown[] {
  const merged = mergePreviewRowsWithFieldPatches(previewRows, patches);
  if (!excludedByRowIndex || Object.keys(excludedByRowIndex).length === 0) return merged;
  return merged.map((r, i) => {
    if (!excludedByRowIndex[i] || r == null || typeof r !== 'object' || Array.isArray(r)) return r;
    return { ...(r as Record<string, unknown>), [SHEET_REVIEW_EXCLUDED_DRAFT_KEY]: true };
  });
}

function stripEmptyUserCategoryIdFromDraftRow(o: Record<string, unknown>): void {
  const v = o.userCategoryId;
  if (v === '' || (typeof v === 'string' && !String(v).trim())) {
    delete o.userCategoryId;
  }
}

function sanitizeDraftRowAfterRematch(row: Record<string, unknown>): Record<string, unknown> {
  const out = { ...row };
  if (shouldStripNullRootMaterialIdForMatch(out, undefined)) {
    delete out.materialId;
  }
  stripEmptyUserCategoryIdFromDraftRow(out);
  return out;
}

function getRematchPickForDraftRow(
  rematchByRowIndex: SheetRematchByRowIndex | undefined,
  row: unknown,
  arrayIndex: number
): SheetRematchPick | undefined {
  if (!rematchByRowIndex || Object.keys(rematchByRowIndex).length === 0) return undefined;
  const byIdx = rematchByRowIndex[arrayIndex];
  if (byIdx) return byIdx;
  if (row != null && typeof row === 'object' && !Array.isArray(row)) {
    const ri = Number((row as Record<string, unknown>).rowIndex);
    if (Number.isFinite(ri)) {
      const byRi = rematchByRowIndex[ri];
      if (byRi) return byRi;
    }
  }
  return undefined;
}

function mergeRematchPickIntoDraftRow(row: Record<string, unknown>, pick: SheetRematchPick): Record<string, unknown> {
  const out = { ...row };
  let uid: number | null = null;
  const ucPick = pick.userCategoryId;
  if (typeof ucPick === 'number' && Number.isFinite(ucPick) && ucPick > 0) {
    uid = ucPick;
  } else if (typeof ucPick === 'string' && ucPick.trim()) {
    const n = Number(ucPick.trim());
    if (Number.isFinite(n) && n > 0) uid = n;
  }
  if (uid != null) {
    out.userCategoryId = uid;
  } else {
    delete out.userCategoryId;
  }
  const rn = typeof pick.rawName === 'string' ? pick.rawName.trim() : '';
  if (rn.length > 0) {
    out.rawName = rn;
    const s = out.source;
    if (s != null && typeof s === 'object' && !Array.isArray(s)) {
      out.source = { ...(s as Record<string, unknown>), rawName: rn };
    } else {
      out.source = { rawName: rn };
    }
  }
  if (pick.createCatalogMaterial === true) {
    out.createCatalogMaterial = true;
  } else {
    delete out.createCatalogMaterial;
  }
  if (pick.rejectSuggestedCatalogMatch === true) {
    out.rejectSuggestedCatalogMatch = true;
  } else {
    delete out.rejectSuggestedCatalogMatch;
  }
  stripEmptyUserCategoryIdFromDraftRow(out);
  return out;
}

export function mergeRematchIntoSheetDraftRows(
  rows: unknown[],
  rematchByRowIndex: SheetRematchByRowIndex | undefined
): unknown[] {
  return rows.map((r, i) => {
    if (r == null || typeof r !== 'object' || Array.isArray(r)) return r;
    const row = r as Record<string, unknown>;
    const pick = getRematchPickForDraftRow(rematchByRowIndex, row, i);
    if (!pick) {
      return sanitizeDraftRowAfterRematch(row);
    }
    return mergeRematchPickIntoDraftRow(row, pick);
  });
}

function shouldStripNullRootMaterialIdForMatch(
  row: Record<string, unknown>,
  pick: SheetRematchPick | undefined
): boolean {
  if (!rowHasPersistableMaterialId(row)) return false;
  if (pick?.rejectSuggestedCatalogMatch === true && pick?.createCatalogMaterial === true) return false;
  return numericMaterialIdFromUnknown(row.materialId) == null;
}

function applyDraftRowEnrichmentForConfirmFromPreview(
  row: Record<string, unknown>,
  pick: SheetRematchPick | undefined
): void {
  const c = readSheetRowCompleteness(row);
  if (c?.canIncludeInConfirm !== true) return;
  if (rowHasPersistableMaterialId(row)) return;
  const uid = resolveSheetConfirmUserCategoryId(row, pick);
  if (uid != null && uid > 0) {
    row.userCategoryId = uid;
  }
  const raw = resolveSheetRematchDisplayedRawName(row, pick).trim();
  if (raw.length > 0) {
    row.rawName = raw;
    const srcBase =
      row.source != null && typeof row.source === 'object' && !Array.isArray(row.source)
        ? { ...(row.source as Record<string, unknown>) }
        : {};
    srcBase.rawName = raw;
    row.source = srcBase;
  }
  const unitLine = readSheetRowUnitDisplay(row);
  const ud = unitLine.trim();
  const canon = ud ? coerceRawUnitToMaterialSelectValue(unitLine) || ud : '';
  if (canon) {
    const srcBase =
      row.source != null && typeof row.source === 'object' && !Array.isArray(row.source)
        ? { ...(row.source as Record<string, unknown>) }
        : {};
    srcBase.rawUnit = canon;
    row.source = srcBase;
    row.rawUnit = canon;
  }
  if (pick?.createCatalogMaterial === true) {
    row.createCatalogMaterial = true;
  } else {
    delete row.createCatalogMaterial;
  }
}

function finalizeSheetDraftRowsForBackend(
  rows: unknown[],
  rematchByRowIndex: SheetRematchByRowIndex | undefined
): unknown[] {
  return rows.map((r, i) => {
    if (r == null || typeof r !== 'object' || Array.isArray(r)) return r;
    const row = { ...(r as Record<string, unknown>) };
    const pick = getRematchPickForDraftRow(rematchByRowIndex, row, i);
    if (shouldStripNullRootMaterialIdForMatch(row, pick)) {
      delete row.materialId;
    }
    stripEmptyUserCategoryIdFromDraftRow(row);
    applyDraftRowEnrichmentForConfirmFromPreview(row, pick);
    stripEmptyUserCategoryIdFromDraftRow(row);
    return row;
  });
}

export function sheetRowFieldPatchAffectsCatalogMatch(patch: Partial<SheetRowFieldPatch>): boolean {
  return patch.rawName !== undefined || patch.unit !== undefined;
}

export function materialMatchTouchedMapAllFalse(rowCount: number): Record<number, boolean> {
  const out: Record<number, boolean> = {};
  for (let i = 0; i < rowCount; i++) out[i] = false;
  return out;
}

export function materialMatchTouchedMapFromDraftRows(rows: unknown[]): Record<number, boolean> {
  const out: Record<number, boolean> = {};
  for (let i = 0; i < rows.length; i++) {
    const r = rows[i];
    if (r != null && typeof r === 'object' && !Array.isArray(r)) {
      const ra = (r as Record<string, unknown>).reanalyzeMaterialMatch;
      out[i] = typeof ra === 'boolean' ? ra : true;
    } else {
      out[i] = true;
    }
  }
  return out;
}

function applyReanalyzeMaterialMatchToDraftRows(
  rows: unknown[],
  touchedByRowIndex: Record<number, boolean>
): unknown[] {
  return rows.map((r, i) => {
    if (r == null || typeof r !== 'object' || Array.isArray(r)) return r;
    const row = { ...(r as Record<string, unknown>) };
    const touched = touchedByRowIndex[i] === true;
    if (rowHasPersistableMaterialId(row)) {
      row.reanalyzeMaterialMatch = touched;
    } else if (touched) {
      row.reanalyzeMaterialMatch = true;
    } else {
      delete row.reanalyzeMaterialMatch;
    }
    return row;
  });
}

export function buildSheetDraftRowsForPut(
  previewRows: unknown[],
  patches: Record<number, SheetRowFieldPatch> | undefined,
  excludedByRowIndex: Record<number, boolean> | undefined,
  rematchByRowIndex: SheetRematchByRowIndex | undefined,
  materialMatchTouchedByRowIndex?: Record<number, boolean>
): unknown[] {
  const base = mergePreviewRowsForDraftSave(previewRows, patches, excludedByRowIndex);
  const merged = mergeRematchIntoSheetDraftRows(base, rematchByRowIndex);
  const finalized = finalizeSheetDraftRowsForBackend(merged, rematchByRowIndex);
  if (materialMatchTouchedByRowIndex == null) return finalized;
  return applyReanalyzeMaterialMatchToDraftRows(finalized, materialMatchTouchedByRowIndex);
}

export function splitSheetReviewExcludedFromLoadedRows(rows: unknown[]): {
  rows: unknown[];
  excludedByRowIndex: Record<number, boolean>;
} {
  const excludedByRowIndex: Record<number, boolean> = {};
  const out = rows.map((r, i) => {
    if (r == null || typeof r !== 'object' || Array.isArray(r)) return r;
    const o = r as Record<string, unknown>;
    if (o[SHEET_REVIEW_EXCLUDED_DRAFT_KEY] === true) {
      excludedByRowIndex[i] = true;
      const next = { ...o };
      delete next[SHEET_REVIEW_EXCLUDED_DRAFT_KEY];
      return next;
    }
    return r;
  });
  return { rows: out, excludedByRowIndex };
}

export function rowNeedsCategoryRematch(row: unknown): boolean {
  return !rowHasPersistableMaterialId(row) || readSheetRowListingMode(row) === 'commerce_listing';
}

function readSheetRowIndexForConfirm(row: unknown, fallbackZeroBased: number): number {
  if (row != null && typeof row === 'object' && !Array.isArray(row)) {
    const n = Number((row as Record<string, unknown>).rowIndex);
    if (Number.isFinite(n)) return n;
  }
  return fallbackZeroBased + 1;
}

function omitSheetReviewUiKeys(base: Record<string, unknown>): Record<string, unknown> {
  const out = { ...base };
  delete out.sheetReviewManual;
  delete out[SHEET_REVIEW_EXCLUDED_DRAFT_KEY];
  return out;
}

export function readSheetRowOfferDefaults(row: unknown): { unquoted: boolean; valueAddedTax: boolean } {
  let unquoted = false;
  let valueAddedTax = true;
  if (row == null || typeof row !== 'object' || Array.isArray(row)) {
    return { unquoted, valueAddedTax };
  }
  const o = row as Record<string, unknown>;
  const da = o.defaultsApplied;
  if (da != null && typeof da === 'object' && !Array.isArray(da)) {
    const d = da as Record<string, unknown>;
    if (typeof d.unquoted === 'boolean') unquoted = d.unquoted;
    if (typeof d.valueAddedTax === 'boolean') valueAddedTax = d.valueAddedTax;
  }
  if (typeof o.unquoted === 'boolean') unquoted = o.unquoted;
  if (typeof o.valueAddedTax === 'boolean') valueAddedTax = o.valueAddedTax;
  return { unquoted, valueAddedTax: unquoted ? false : valueAddedTax };
}

export function readSheetRowCatalogMaterialId(row: unknown): number | null {
  if (row == null || typeof row !== 'object' || Array.isArray(row)) return null;
  const o = row as Record<string, unknown>;
  const root = numericMaterialIdFromUnknown(o.materialId);
  if (root != null && root > 0) return root;
  const m = o.match;
  if (m != null && typeof m === 'object' && !Array.isArray(m)) {
    const mid = numericMaterialIdFromUnknown((m as Record<string, unknown>).materialId);
    if (mid != null && mid > 0) return mid;
  }
  return null;
}

function materialIdForConfirmRow(row: Record<string, unknown>): number | null {
  return readSheetRowCatalogMaterialId(row);
}

export type CommerceListingOfferSnapshot = {
  price: number | null;
  unquoted: boolean;
  valueAddedTax: boolean;
  promotionNorm: string;
  promotionDiscount: number | null;
};

function sheetPricesCloseForListingCompare(a: number | null, b: number | null): boolean {
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  return Math.abs(a - b) < 0.02;
}

function promotionNormForSheetCompare(row: unknown): string {
  if (row == null || typeof row !== 'object' || Array.isArray(row)) return '';
  const o = row as Record<string, unknown>;
  if (typeof o.promotion === 'string' && o.promotion.trim()) return o.promotion.trim().toLowerCase();
  return getSheetRowPromotionSeedFromSource(row).trim().toLowerCase();
}

export function buildCommerceListingOfferByCatalogMaterialId(
  categories: ParsedCategory[]
): Map<number, CommerceListingOfferSnapshot> {
  const out = new Map<number, CommerceListingOfferSnapshot>();
  for (const cat of categories) {
    for (const mat of cat.materials) {
      const mc = mat.commerceRow;
      if (mc == null) continue;
      const id = Number(mat.id);
      if (!Number.isFinite(id) || id <= 0) continue;
      const vat =
        mc.value_added_tax === true || mc.value_added_tax === false ? mc.value_added_tax : true;
      const promo = (mc.promotion ?? '').trim().toLowerCase();
      out.set(id, {
        price: mc.price,
        unquoted: mc.unquoted,
        valueAddedTax: vat,
        promotionNorm: promo,
        promotionDiscount: mc.promotion_discount,
      });
    }
  }
  return out;
}

export function sheetMergedRowMatchesCommerceListing(
  effRow: unknown,
  listing: CommerceListingOfferSnapshot
): boolean {
  const sheetPrice = readSheetRowPriceValue(effRow);
  if (!sheetPricesCloseForListingCompare(sheetPrice, listing.price)) return false;
  const defs = readSheetRowOfferDefaults(effRow);
  if (defs.unquoted !== listing.unquoted) return false;
  if (defs.valueAddedTax !== listing.valueAddedTax) return false;
  if (promotionNormForSheetCompare(effRow) !== listing.promotionNorm) return false;
  const sd = readSheetRowPromotionDiscountValue(effRow);
  const ld = listing.promotionDiscount;
  if (sd == null && ld == null) return true;
  if (sd == null || ld == null) return false;
  return Math.abs(sd - ld) < 0.0001;
}

export function computeUnchangedCommerceListingExclusions(
  mergedPreviewRows: unknown[],
  byMaterialId: Map<number, CommerceListingOfferSnapshot>
): Record<number, boolean> {
  const out: Record<number, boolean> = {};
  mergedPreviewRows.forEach((row, i) => {
    const mid = readSheetRowCatalogMaterialId(row);
    if (mid == null || mid <= 0) return;
    const listing = byMaterialId.get(mid);
    if (listing == null) return;
    if (sheetMergedRowMatchesCommerceListing(row, listing)) out[i] = true;
  });
  return out;
}

export function computeUnchangedCommerceListingExclusionsFromResponse(
  mergedPreviewRows: unknown[],
  materialsApiBody: unknown,
  commerceId: number
): Record<number, boolean> {
  const raw = extractCommerceMaterialsDataArray(materialsApiBody);
  if (raw == null || !Array.isArray(raw)) return {};
  const cats = parseCommerceMaterialsPayload(raw, commerceId);
  const byMat = buildCommerceListingOfferByCatalogMaterialId(cats);
  return computeUnchangedCommerceListingExclusions(mergedPreviewRows, byMat);
}

function readPromotionForConfirm(base: Record<string, unknown>): string | null {
  if (!Object.prototype.hasOwnProperty.call(base, 'promotion')) return null;
  if (typeof base.promotion !== 'string') return null;
  const t = base.promotion.trim();
  return t.length > 0 ? t : null;
}

function readDescriptionForConfirm(base: Record<string, unknown>): string | null {
  if (!Object.prototype.hasOwnProperty.call(base, 'description')) return null;
  if (typeof base.description !== 'string') return null;
  const t = base.description.trim();
  return t.length > 0 ? t : null;
}

function readPromotionDiscountForConfirm(base: Record<string, unknown>): number | null {
  return readSheetRowPromotionDiscountValue(base);
}

function attachOptionalOfferFieldsForConfirm(target: Record<string, unknown>, base: Record<string, unknown>) {
  const promo = readPromotionForConfirm(base);
  if (promo) target.promotion = promo;
  else delete target.promotion;
  const desc = readDescriptionForConfirm(base);
  if (desc) target.description = desc;
  else delete target.description;
  const disc = readPromotionDiscountForConfirm(base);
  if (disc != null) target.promotionDiscount = disc;
  else delete target.promotionDiscount;
}

export function getSheetRowDescriptionSeedFromSource(row: unknown): string {
  if (row == null || typeof row !== 'object' || Array.isArray(row)) return '';
  const o = row as Record<string, unknown>;
  if (typeof o.description === 'string' && o.description.trim()) return o.description.trim();
  const src = o.source;
  if (src == null || typeof src !== 'object' || Array.isArray(src)) return '';
  const s = src as Record<string, unknown>;
  const extra =
    typeof s.extractedExtraDescription === 'string' && s.extractedExtraDescription.trim()
      ? s.extractedExtraDescription.trim()
      : '';
  const note = typeof s.extractedNote === 'string' && s.extractedNote.trim() ? s.extractedNote.trim() : '';
  const parts: string[] = [];
  if (extra) parts.push(extra);
  if (note) parts.push(note);
  return parts.length > 0 ? parts.join('\n\n') : '';
}

export function getSheetRowPromotionSeedFromSource(row: unknown): string {
  if (row == null || typeof row !== 'object' || Array.isArray(row)) return '';
  const o = row as Record<string, unknown>;
  if (typeof o.promotion === 'string' && o.promotion.trim()) return o.promotion.trim();
  const src = o.source;
  if (src == null || typeof src !== 'object' || Array.isArray(src)) return '';
  const p = (src as Record<string, unknown>).extractedPromo;
  return typeof p === 'string' && p.trim() ? p.trim() : '';
}

export function getSheetRowPromotionDiscountSeedFromRow(row: unknown): string {
  const n = readSheetRowPromotionDiscountValue(row);
  if (n != null) return formatSheetRowPromotionDiscountInputValue(n);
  if (row == null || typeof row !== 'object' || Array.isArray(row)) return '';
  const o = row as Record<string, unknown>;
  if (typeof o.promotionDiscount === 'string' && o.promotionDiscount.trim()) {
    const t = o.promotionDiscount.trim();
    const parsed = parseSheetPromotionDiscountInputToNumber(t);
    if (parsed != null) return formatSheetRowPromotionDiscountInputValue(parsed);
    return t;
  }
  return '';
}

function buildSourceForRematchConfirm(base: Record<string, unknown>, rawName: string): Record<string, unknown> {
  const prev =
    base.source != null && typeof base.source === 'object' && !Array.isArray(base.source)
      ? { ...(base.source as Record<string, unknown>) }
      : {};
  prev.rawName = rawName;
  if (prev.rawPrice === undefined) {
    const pv = readSheetRowPriceValue(base);
    if (pv != null) prev.rawPrice = pv;
  }
  if (prev.rawUnit === undefined || prev.rawUnit === '') {
    const u = readSheetRowUnitDisplay(base);
    if (u) prev.rawUnit = u;
  }
  if (prev.rawAmount === undefined) {
    const a = readSheetRowAmountDisplay(base);
    if (a) {
      const n = Number(String(a).replace(',', '.'));
      prev.rawAmount = Number.isFinite(n) ? n : a;
    }
  }
  return prev;
}

function shapeCatalogLinkedConfirmRow(
  base: Record<string, unknown>,
  rowIndex: number,
  materialId: number,
  userCategoryId: number | null
): Record<string, unknown> {
  const { unquoted, valueAddedTax } = readSheetRowOfferDefaults(base);
  const out: Record<string, unknown> = { ...base };
  out.rowIndex = rowIndex;
  out.materialId = materialId;
  out.unquoted = unquoted;
  out.valueAddedTax = valueAddedTax;
  attachOptionalOfferFieldsForConfirm(out, base);
  if (base.defaultsApplied != null && typeof base.defaultsApplied === 'object') {
    out.defaultsApplied = base.defaultsApplied;
  }
  if (userCategoryId != null && userCategoryId > 0) {
    out.userCategoryId = userCategoryId;
  } else {
    delete out.userCategoryId;
  }
  return out;
}

function shapeRejectSuggestedCatalogCreateRow(
  base: Record<string, unknown>,
  rowIndex: number,
  userCategoryId: number,
  rawName: string,
  rawUnitCanonical: string
): Record<string, unknown> {
  const { unquoted, valueAddedTax } = readSheetRowOfferDefaults(base);
  const source = buildSourceForRematchConfirm(base, rawName);
  source.rawUnit = rawUnitCanonical;
  const out: Record<string, unknown> = {
    rowIndex,
    materialId: null,
    rejectSuggestedCatalogMatch: true,
    userCategoryId,
    rawName,
    rawUnit: rawUnitCanonical,
    source,
    unquoted,
    valueAddedTax,
    createCatalogMaterial: true,
  };
  const price = readSheetRowPriceValue(base);
  if (price != null) out.price = price;
  attachOptionalOfferFieldsForConfirm(out, base);
  if (base.defaultsApplied != null && typeof base.defaultsApplied === 'object') {
    out.defaultsApplied = base.defaultsApplied;
  }
  return out;
}

function shapeRematchConfirmRow(
  base: Record<string, unknown>,
  rowIndex: number,
  userCategoryId: number,
  rawName: string,
  createCatalogMaterial?: boolean
): Record<string, unknown> {
  const { unquoted, valueAddedTax } = readSheetRowOfferDefaults(base);
  const source = buildSourceForRematchConfirm(base, rawName);
  const out: Record<string, unknown> = {
    ...base,
    rowIndex,
    userCategoryId,
    rawName,
    source,
    unquoted,
    valueAddedTax,
  };
  attachOptionalOfferFieldsForConfirm(out, base);
  if (base.defaultsApplied != null && typeof base.defaultsApplied === 'object') {
    out.defaultsApplied = base.defaultsApplied;
  }
  if (createCatalogMaterial === true) {
    out.createCatalogMaterial = true;
  }
  return out;
}

export function buildRowsForCommerceSheetConfirm(
  previewRows: unknown[],
  rematchByRowIndex: SheetRematchByRowIndex,
  excludedByRowIndex?: Record<number, boolean>
): { rows: unknown[]; error: string | null } {
  const out: unknown[] = [];
  let missingRematch = 0;
  let missingItemName = 0;
  let missingResolvedPublish = 0;
  let missingRejectCatalogUnit = 0;
  for (let i = 0; i < previewRows.length; i++) {
    if (excludedByRowIndex?.[i]) continue;
    const row = previewRows[i];
    if (row == null || typeof row !== 'object' || Array.isArray(row)) continue;
    const rowObj = omitSheetReviewUiKeys(row as Record<string, unknown>);
    const rowIndex = readSheetRowIndexForConfirm(row, i);
    if (rowUsesCatalogDirectConfirm(row)) {
      const catPick = rematchByRowIndex[i];
      const catId = resolveSheetConfirmUserCategoryId(rowObj, catPick);
      if (catId == null) {
        missingRematch += 1;
        continue;
      }
      if (!getSheetRowDefaultRawName(rowObj).trim()) {
        missingItemName += 1;
        continue;
      }
      const rejectCreate =
        catPick?.rejectSuggestedCatalogMatch === true && catPick?.createCatalogMaterial === true;
      if (rejectCreate) {
        const unitCanon = coerceRawUnitToMaterialSelectValue(readSheetRowUnitDisplay(rowObj));
        if (!unitCanon) {
          missingRejectCatalogUnit += 1;
          continue;
        }
        out.push(
          shapeRejectSuggestedCatalogCreateRow(
            rowObj,
            rowIndex,
            catId,
            getSheetRowDefaultRawName(rowObj).trim(),
            unitCanon
          )
        );
        continue;
      }
      const mid = materialIdForConfirmRow(rowObj);
      if (mid == null) {
        missingResolvedPublish += 1;
        continue;
      }
      out.push(shapeCatalogLinkedConfirmRow(rowObj, rowIndex, mid, catId));
      continue;
    }
    const pick = rematchByRowIndex[i];
    const cid = resolveSheetConfirmUserCategoryId(rowObj, pick);
    const raw = resolveSheetRematchDisplayedRawName(rowObj, pick);
    if (cid != null && cid > 0 && raw.length > 0) {
      const createCat = pick?.createCatalogMaterial === true;
      out.push(shapeRematchConfirmRow(rowObj, rowIndex, cid, raw, createCat));
      continue;
    }
    missingRematch += 1;
  }
  if (previewRows.length > 0 && previewRows.every((_, idx) => Boolean(excludedByRowIndex?.[idx]))) {
    return { rows: [], error: 'Incluí al menos un ítem (primera columna) para poder guardar el listado.' };
  }
  if (missingRematch > 0 || missingItemName > 0 || missingResolvedPublish > 0 || missingRejectCatalogUnit > 0) {
    const parts: string[] = [];
    if (missingRematch > 0) {
      parts.push(
        missingRematch === 1
          ? 'categoría y nombre del ítem en 1 fila'
          : `categoría y nombre del ítem en ${missingRematch} filas`
      );
    }
    if (missingItemName > 0) {
      parts.push(
        missingItemName === 1
          ? 'el nombre del ítem en 1 fila'
          : `el nombre del ítem en ${missingItemName} filas`
      );
    }
    if (missingResolvedPublish > 0) {
      parts.push(
        missingResolvedPublish === 1
          ? 'los datos en 1 fila (no alcanzan para publicar)'
          : `los datos en ${missingResolvedPublish} filas (no alcanzan para publicar)`
      );
    }
    if (missingRejectCatalogUnit > 0) {
      parts.push(
        missingRejectCatalogUnit === 1
          ? 'una unidad válida en el desplegable en la fila donde marcaste que no es el material sugerido'
          : `unidad válida en el desplegable en ${missingRejectCatalogUnit} filas donde marcaste que no es el material sugerido`
      );
    }
    return {
      rows: [],
      error: `Completá ${parts.join(' y ')} antes de confirmar.`,
    };
  }
  if (out.length === 0) {
    return { rows: [], error: 'No hay filas para confirmar.' };
  }
  return { rows: out, error: null };
}

export type CommerceMaterialSingleItemCreateInput = {
  materialId: number;
  userCategoryId: number;
  rawName: string;
  price: number | null;
  unquoted: boolean;
  valueAddedTax: boolean;
  promotion: string | null;
  promotionDiscount: number | null;
};

export type ManualMaterialSubmitPayload =
  | { kind: 'freeform'; userCategoryId: number; rawName: string; initialPatch: Partial<SheetRowFieldPatch> }
  | { kind: 'catalog'; input: CommerceMaterialSingleItemCreateInput };

export function buildCommerceMaterialSingleItemConfirmRow(
  input: CommerceMaterialSingleItemCreateInput
): Record<string, unknown> {
  const rawName = input.rawName.trim();
  if (!rawName) {
    throw new Error('Nombre del material requerido.');
  }
  if (!Number.isFinite(input.materialId) || input.materialId <= 0) {
    throw new Error('Material inválido.');
  }
  if (!Number.isFinite(input.userCategoryId) || input.userCategoryId <= 0) {
    throw new Error('Categoría inválida.');
  }
  const source: Record<string, unknown> = { rawName };
  if (input.price != null && Number.isFinite(input.price)) {
    source.rawPrice = input.price;
  }
  const base: Record<string, unknown> = {
    rowIndex: 0,
    materialId: input.materialId,
    userCategoryId: input.userCategoryId,
    rawName,
    match: {
      listingMode: 'catalog_linked',
      materialId: input.materialId,
      inferredCategoryId: input.userCategoryId,
    },
    unquoted: input.unquoted,
    valueAddedTax: input.unquoted ? false : input.valueAddedTax,
    source,
  };
  if (input.price != null && Number.isFinite(input.price)) {
    base.price = input.price;
  }
  if (input.promotion?.trim()) base.promotion = input.promotion.trim();
  if (input.promotionDiscount != null && Number.isFinite(input.promotionDiscount)) {
    base.promotionDiscount = input.promotionDiscount;
  }
  const built = buildRowsForCommerceSheetConfirm([base], {}, undefined);
  if (built.error) {
    throw new Error(built.error);
  }
  if (built.rows.length !== 1 || built.rows[0] == null) {
    throw new Error('No se pudo armar la fila para el servidor.');
  }
  return built.rows[0] as Record<string, unknown>;
}

export type CommerceMaterialSingleItemManualCreateInput = {
  userCategoryId: number;
  rawName: string;
  rawUnit: string;
  description?: string;
  promotion?: string;
  promotionDiscount?: number | null;
  price?: number | null;
  unquoted: boolean;
  valueAddedTax: boolean;
};

export function buildCommerceMaterialRowFromManualMaterialSubmitPayload(
  payload: ManualMaterialSubmitPayload
): Record<string, unknown> {
  if (payload.kind === 'catalog') {
    return buildCommerceMaterialSingleItemConfirmRow(payload.input);
  }
  const patch = payload.initialPatch;
  const rawUnit = (patch.unit ?? '').trim();
  let price: number | null = null;
  if (patch.unquoted !== true && patch.price != null && String(patch.price).trim()) {
    const pn = parseSheetPriceInputToNumber(String(patch.price));
    if (pn != null) price = pn;
  }
  let promotionDiscount: number | null = null;
  if (patch.promotionDiscount != null && String(patch.promotionDiscount).trim()) {
    promotionDiscount = parseSheetPromotionDiscountInputToNumber(String(patch.promotionDiscount));
  }
  return buildCommerceMaterialSingleItemManualCreateRow({
    userCategoryId: payload.userCategoryId,
    rawName: payload.rawName,
    rawUnit,
    description: patch.description,
    promotion: patch.promotion,
    promotionDiscount,
    price,
    unquoted: patch.unquoted === true,
    valueAddedTax: patch.valueAddedTax !== false,
  });
}

export function buildCommerceMaterialSingleItemManualCreateRow(
  input: CommerceMaterialSingleItemManualCreateInput
): Record<string, unknown> {
  const rawName = input.rawName.trim();
  if (!rawName) {
    throw new Error('Nombre del material requerido.');
  }
  if (!Number.isFinite(input.userCategoryId) || input.userCategoryId <= 0) {
    throw new Error('Categoría inválida.');
  }
  const unitCanon = coerceRawUnitToMaterialSelectValue(String(input.rawUnit ?? '').trim());
  if (!unitCanon) {
    throw new Error('Elegí una unidad válida del listado.');
  }
  const base: Record<string, unknown> = {
    ...createManualSheetReviewRow(1),
    rawName,
    source: { rawName, rawUnit: unitCanon },
    rawUnit: unitCanon,
    unquoted: input.unquoted,
    valueAddedTax: input.unquoted ? false : input.valueAddedTax,
  };
  if (input.description?.trim()) {
    base.description = input.description.trim();
  }
  if (input.promotion?.trim()) {
    base.promotion = input.promotion.trim();
  }
  if (input.promotionDiscount != null && Number.isFinite(input.promotionDiscount)) {
    base.promotionDiscount = input.promotionDiscount;
  }
  if (input.price != null && Number.isFinite(input.price)) {
    base.price = input.price;
    const src = base.source != null && typeof base.source === 'object' && !Array.isArray(base.source)
      ? { ...(base.source as Record<string, unknown>) }
      : {};
    src.rawName = rawName;
    src.rawUnit = unitCanon;
    src.rawPrice = input.price;
    base.source = src;
  }
  const rematch: SheetRematchByRowIndex = {
    0: {
      userCategoryId: input.userCategoryId,
      rawName,
      createCatalogMaterial: true,
      rejectSuggestedCatalogMatch: false,
    },
  };
  const built = buildRowsForCommerceSheetConfirm([base], rematch, undefined);
  if (built.error) {
    throw new Error(built.error);
  }
  if (built.rows.length !== 1 || built.rows[0] == null) {
    throw new Error('No se pudo armar la fila para el servidor.');
  }
  return built.rows[0] as Record<string, unknown>;
}

export function formatDocumentAssessmentForUser(ass: SheetPreviewDocumentAssessment | null | undefined): string {
  if (!ass) {
    return 'El archivo no encaja como lista de materiales para este flujo.';
  }
  const parts = [ass.titleForUser, ass.bodyForUser, ass.contentSummary, ass.irrelevanceReason].filter(
    (x): x is string => typeof x === 'string' && x.trim().length > 0
  );
  return parts.length ? parts.join('\n\n') : 'El archivo no encaja como lista de materiales para este flujo.';
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function getAuthHeaders() {
  const authToken = localStorage.getItem('access-Token-Edify');
  return { headers: { Authorization: `Bearer ${authToken}` } };
}

export function extractSheetPreviewAsyncMeta(raw: unknown): { jobId: string | null } {
  const inner = unwrapPreviewData(raw);
  if (!inner) return { jobId: null };
  const j = inner.jobId;
  let jobId: string | null = null;
  if (typeof j === 'string' && j.trim()) jobId = j.trim();
  else if (typeof j === 'number' && Number.isFinite(j)) jobId = String(j);
  return { jobId };
}

export type SheetPreviewJobStatus = {
  phase: string;
  detailPhase?: string | null;
  errorMessage?: string;
  previewResult?: unknown;
  ingestionProgress?: SheetPreviewIngestionProgress | null;
  updatedAt?: string | null;
};

export function parseSheetPreviewJobPayload(raw: unknown): SheetPreviewJobStatus {
  const inner = unwrapPreviewData(raw);
  if (!inner) return { phase: 'queued' };
  const phase =
    typeof inner.phase === 'string' && inner.phase.trim().length > 0 ? inner.phase.trim() : 'queued';
  let detailPhase: string | null | undefined;
  if (inner.detailPhase === null) detailPhase = null;
  else if (typeof inner.detailPhase === 'string') detailPhase = inner.detailPhase.trim() || null;
  const errorMessage =
    typeof inner.errorMessage === 'string' && inner.errorMessage.trim().length > 0
      ? inner.errorMessage.trim()
      : undefined;
  const previewResult = inner.previewResult !== undefined ? inner.previewResult : undefined;
  const ingestionProgress = parseIngestionProgress(inner.ingestionProgress);
  const ua = inner.updatedAt;
  const updatedAt =
    typeof ua === 'string' && ua.trim().length > 0 ? ua.trim() : ua === null ? null : undefined;
  return { phase, detailPhase, errorMessage, previewResult, ingestionProgress, updatedAt };
}

function jobPollLoaderPhase(status: SheetPreviewJobStatus): string {
  const d = status.detailPhase;
  if (typeof d === 'string' && d.trim().length > 0) return d.trim();
  return status.phase;
}

function previewPayloadFromJobOrPipeline(pipelineRaw: unknown): SheetPreviewPayload | null {
  const inner = unwrapPreviewData(pipelineRaw);
  if (!inner) return getSheetPreviewPayload(pipelineRaw);
  const pr = inner.previewResult;
  if (pr != null) {
    const fromResult = getSheetPreviewPayload(pr);
    if (fromResult && (fromResult.status === 'ready' || fromResult.status === 'rejected' || fromResult.rows.length > 0)) {
      return fromResult;
    }
  }
  const direct = getSheetPreviewPayload(inner);
  if (direct && (direct.status === 'ready' || direct.status === 'rejected' || direct.rows.length > 0)) {
    return direct;
  }
  for (const k of ['result', 'preview', 'sheetPreview', 'payload']) {
    const v = inner[k];
    if (v != null) {
      const p = getSheetPreviewPayload(v);
      if (p && (p.status === 'ready' || p.status === 'rejected' || p.rows.length > 0)) {
        return p;
      }
    }
  }
  return direct ?? getSheetPreviewPayload(pipelineRaw);
}

export type SheetPreviewRowsPaginationMeta = {
  page: number;
  pageSize: number;
  totalRows: number;
  totalPages: number;
};

function readPreviewRowsPagination(previewResult: unknown): SheetPreviewRowsPaginationMeta | null {
  if (previewResult == null || typeof previewResult !== 'object' || Array.isArray(previewResult)) return null;
  const pr = previewResult as Record<string, unknown>;
  const raw = pr.previewRowsPagination;
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const page = Number(o.page);
  const pageSize = Number(o.pageSize);
  const totalRows = Number(o.totalRows);
  const totalPages = Number(o.totalPages);
  if (!Number.isFinite(totalPages) || totalPages < 1) return null;
  return {
    page: Number.isFinite(page) ? page : 1,
    pageSize: Number.isFinite(pageSize) ? pageSize : 0,
    totalRows: Number.isFinite(totalRows) ? totalRows : 0,
    totalPages,
  };
}

function replaceJobPayloadPreviewResult(
  jobPayload: unknown,
  mergedPreviewResult: Record<string, unknown>
): unknown {
  if (jobPayload == null || typeof jobPayload !== 'object' || Array.isArray(jobPayload)) return jobPayload;
  const root = jobPayload as Record<string, unknown>;
  const d = root.data;
  if (d != null && typeof d === 'object' && !Array.isArray(d)) {
    return { ...root, data: { ...(d as Record<string, unknown>), previewResult: mergedPreviewResult } };
  }
  return { ...root, previewResult: mergedPreviewResult };
}

export async function fetchCommerceCatalogMaterialsParsed(
  commerceId: number,
  opts?: { pageSize?: number; q?: string; signal?: AbortSignal }
): Promise<ParsedCategory[]> {
  const pageSize = Math.min(200, Math.max(1, opts?.pageSize ?? COMMERCE_CATALOG_MATERIALS_PAGE_SIZE));
  let page = 1;
  let totalPages = 1;
  const allItems: unknown[] = [];
  do {
    if (opts?.signal?.aborted) {
      const err = new Error('Aborted') as Error & { name?: string };
      err.name = 'AbortError';
      throw err;
    }
    const res = await getCommerceCatalogMaterials(commerceId, { page, pageSize, q: opts?.q });
    const meta = unwrapCommerceCatalogMaterialsPage(res.data);
    if (meta == null) return [];
    allItems.push(...meta.items);
    totalPages = meta.totalPages > 0 ? meta.totalPages : 1;
    page += 1;
  } while (page <= totalPages);
  return parseCatalogMaterialItemsToParsedCategories(allItems, commerceId);
}

export function collectSheetPreviewCatalogMaterialIds(previewRows: unknown): Set<number> {
  const s = new Set<number>();
  if (!Array.isArray(previewRows)) return s;
  for (const row of previewRows) {
    const mid = readSheetRowCatalogMaterialId(row);
    if (mid != null && mid > 0) s.add(mid);
  }
  return s;
}

export async function fetchCommerceCatalogMaterialsForPreviewListing(
  commerceId: number,
  previewRows: unknown,
  opts?: { pageSize?: number; signal?: AbortSignal }
): Promise<ParsedCategory[]> {
  const needed = collectSheetPreviewCatalogMaterialIds(previewRows);
  if (needed.size === 0) return [];
  const pageSize = Math.min(200, Math.max(1, opts?.pageSize ?? COMMERCE_CATALOG_MATERIALS_PAGE_SIZE));
  const collected: unknown[] = [];
  const found = new Set<number>();
  let page = 1;
  let totalPages = 1;
  do {
    if (opts?.signal?.aborted) {
      const err = new Error('Aborted') as Error & { name?: string };
      err.name = 'AbortError';
      throw err;
    }
    const res = await getCommerceCatalogMaterials(commerceId, { page, pageSize });
    const meta = unwrapCommerceCatalogMaterialsPage(res.data);
    if (meta == null) break;
    totalPages = meta.totalPages > 0 ? meta.totalPages : 1;
    for (const item of meta.items) {
      if (item == null || typeof item !== 'object' || Array.isArray(item)) continue;
      const o = item as Record<string, unknown>;
      const midRaw = o.materialId ?? o.material_id;
      const mid = typeof midRaw === 'number' && Number.isFinite(midRaw) ? midRaw : Number(midRaw);
      if (!Number.isFinite(mid) || mid <= 0) continue;
      if (needed.has(mid) && !found.has(mid)) {
        collected.push(item);
        found.add(mid);
      }
    }
    page += 1;
    if (found.size >= needed.size) break;
  } while (page <= totalPages);

  return parseCatalogMaterialItemsToParsedCategories(collected, commerceId);
}

export function computeUnchangedCommerceListingExclusionsFromParsedCategories(
  mergedPreviewRows: unknown[],
  categories: ParsedCategory[]
): Record<number, boolean> {
  const byMat = buildCommerceListingOfferByCatalogMaterialId(categories);
  return computeUnchangedCommerceListingExclusions(mergedPreviewRows, byMat);
}

export async function postCommerceSheetPreview(
  commerceId: number,
  file: File,
  opts?: { previewRowsPage?: number; previewRowsPageSize?: number }
) {
  const formData = new FormData();
  formData.append('file', file);
  const params: Record<string, string> = { dryRun: 'true' };
  if (opts?.previewRowsPageSize != null) {
    params.previewRowsPageSize = String(opts.previewRowsPageSize);
  }
  if (opts?.previewRowsPage != null) {
    params.previewRowsPage = String(opts.previewRowsPage);
  }
  return api.post(`/material-commerce/commerce/${commerceId}/sheet-preview`, formData, {
    params,
    ...getAuthHeaders(),
  });
}

export async function postCommerceSheetPreviewAsync(commerceId: number, file: File) {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/material-commerce/commerce/${commerceId}/sheet-preview-async`, formData, {
    ...getAuthHeaders(),
  });
}

export async function getSheetPreviewJob(
  commerceId: number,
  jobId: string,
  opts?: { previewRowsPage?: number; previewRowsPageSize?: number }
) {
  const params: Record<string, string> = {};
  if (opts?.previewRowsPageSize != null) {
    params.previewRowsPageSize = String(opts.previewRowsPageSize);
  }
  if (opts?.previewRowsPage != null) {
    params.previewRowsPage = String(opts.previewRowsPage);
  }
  const hasParams = Object.keys(params).length > 0;
  return api.get(
    `/material-commerce/commerce/${commerceId}/sheet-preview-jobs/${encodeURIComponent(jobId)}`,
    hasParams ? { ...getAuthHeaders(), params } : getAuthHeaders()
  );
}

async function mergePaginatedSheetPreviewJobPages(
  commerceId: number,
  jobId: string,
  jobPayload: unknown,
  pageSize: number,
  signal?: AbortSignal
): Promise<unknown> {
  const inner = unwrapPreviewData(jobPayload);
  if (!inner) return jobPayload;
  const pr0 = inner.previewResult;
  if (pr0 == null || typeof pr0 !== 'object' || Array.isArray(pr0)) return jobPayload;
  const prObj = pr0 as Record<string, unknown>;
  const pag = readPreviewRowsPagination(pr0);
  if (!pag || pag.totalPages <= 1) return jobPayload;

  const effectivePageSize = pag.pageSize > 0 ? pag.pageSize : pageSize;
  const baseRows = Array.isArray(prObj.rows) ? [...prObj.rows] : [];
  for (let page = 2; page <= pag.totalPages; page += 1) {
    if (signal?.aborted) {
      const err = new Error('Aborted') as Error & { name?: string };
      err.name = 'AbortError';
      throw err;
    }
    const res = await getSheetPreviewJob(commerceId, jobId, {
      previewRowsPageSize: effectivePageSize,
      previewRowsPage: page,
    });
    const innerN = unwrapPreviewData(res.data);
    const prN = innerN?.previewResult;
    if (prN != null && typeof prN === 'object' && !Array.isArray(prN)) {
      const rows = (prN as Record<string, unknown>).rows;
      if (Array.isArray(rows)) baseRows.push(...rows);
    }
  }
  const mergedPr: Record<string, unknown> = {
    ...prObj,
    rows: baseRows,
    previewRowsPagination: {
      page: 1,
      pageSize: pag.pageSize,
      totalRows: pag.totalRows,
      totalPages: 1,
    },
  };
  return replaceJobPayloadPreviewResult(jobPayload, mergedPr);
}

async function fetchAllSyncPreviewRowPages(
  commerceId: number,
  file: File,
  pageSize: number,
  signal?: AbortSignal
): Promise<SheetPreviewPayload | null> {
  const res1 = await postCommerceSheetPreview(commerceId, file, {
    previewRowsPage: 1,
    previewRowsPageSize: pageSize,
  });
  const payload1 = getSheetPreviewPayload(res1.data);
  const inner1 = unwrapPreviewData(res1.data);
  const pr1 = inner1?.previewResult;
  const pag = readPreviewRowsPagination(pr1);
  const rows = Array.isArray(payload1?.rows) ? [...payload1.rows] : [];
  if (!pag || pag.totalPages <= 1) {
    return payload1;
  }
  const effectivePageSize = pag.pageSize > 0 ? pag.pageSize : pageSize;
  for (let page = 2; page <= pag.totalPages; page += 1) {
    if (signal?.aborted) {
      const err = new Error('Aborted') as Error & { name?: string };
      err.name = 'AbortError';
      throw err;
    }
    const res = await postCommerceSheetPreview(commerceId, file, {
      previewRowsPage: page,
      previewRowsPageSize: effectivePageSize,
    });
    const pay = getSheetPreviewPayload(res.data);
    if (pay != null && Array.isArray(pay.rows)) rows.push(...pay.rows);
  }
  if (payload1 == null) return null;
  return { ...payload1, rows };
}

function isSheetPreviewCatalogMatchPhase(status: SheetPreviewJobStatus): boolean {
  return (
    status.phase === 'processing' &&
    (status.detailPhase ?? '').trim().toLowerCase() === 'catalog_match'
  );
}

function catalogMatchProgressFingerprint(status: SheetPreviewJobStatus, rawBody: unknown): string {
  const processed = status.ingestionProgress?.catalogMatchRows?.processed;
  const p = typeof processed === 'number' && Number.isFinite(processed) ? String(processed) : '';
  const u = status.updatedAt != null && status.updatedAt !== '' ? status.updatedAt : '';
  if (p !== '' || u !== '') {
    return `${p}\t${u}`;
  }
  const inner = unwrapPreviewData(rawBody);
  if (inner == null) return '';
  try {
    return JSON.stringify(inner);
  } catch {
    return `${status.phase}\t${status.detailPhase ?? ''}`;
  }
}

export async function waitForSheetPreviewJob(
  commerceId: number,
  jobId: string,
  onUpdate?: (u: SheetPreviewJobPollUpdate) => void,
  options?: {
    maxWaitMs?: number;
    intervalMs?: number;
    signal?: AbortSignal;
    catalogPhaseMaxWaitMs?: number;
    catalogStallMs?: number;
  }
): Promise<unknown> {
  const maxWaitMs = options?.maxWaitMs ?? SHEET_PREVIEW_POLL_MAX_MS;
  const catalogPhaseMaxWaitMs = options?.catalogPhaseMaxWaitMs ?? SHEET_PREVIEW_POLL_CATALOG_PHASE_MAX_MS;
  const catalogStallMs = options?.catalogStallMs ?? SHEET_PREVIEW_CATALOG_STALL_MS;
  const intervalMs = options?.intervalMs ?? SHEET_PREVIEW_POLL_INTERVAL_MS;
  const signal = options?.signal;
  const sessionStart = Date.now();
  const documentDeadline = sessionStart + maxWaitMs;
  let catalogPhaseEnteredAt: number | null = null;
  let catalogStallUntil = 0;
  let lastCatalogFingerprint: string | null = null;

  const timeoutMessage = 'El análisis tardó demasiado. Intentá de nuevo en unos minutos.';
  const stallMessage =
    'El análisis del catálogo no avanzó en varios minutos. Intentá de nuevo en unos minutos.';

  while (true) {
    if (signal?.aborted) {
      const err = new Error('Aborted') as Error & { name?: string };
      err.name = 'AbortError';
      throw err;
    }
    const response = await getSheetPreviewJob(commerceId, jobId);
    const afterPoll = Date.now();
    const status = parseSheetPreviewJobPayload(response.data);
    const ingestionProgressLabel = formatSheetPreviewIngestionProgressForLoader(
      status.ingestionProgress,
      status.detailPhase
    );
    onUpdate?.({
      loaderPhase: jobPollLoaderPhase(status),
      phase: status.phase,
      detailPhase: status.detailPhase,
      ingestionProgressLabel,
    });
    if (status.phase === 'ready') {
      return response.data;
    }
    if (status.phase === 'failed') {
      const msg = status.errorMessage && status.errorMessage.length > 0 ? status.errorMessage : 'El análisis del listado falló.';
      throw new Error(msg);
    }

    const inCatalog = isSheetPreviewCatalogMatchPhase(status);
    if (inCatalog) {
      if (catalogPhaseEnteredAt == null) {
        catalogPhaseEnteredAt = afterPoll;
        catalogStallUntil = afterPoll + catalogStallMs;
      }
      const fp = catalogMatchProgressFingerprint(status, response.data);
      if (lastCatalogFingerprint === null || fp !== lastCatalogFingerprint) {
        lastCatalogFingerprint = fp;
        catalogStallUntil = afterPoll + catalogStallMs;
      }
      if (afterPoll > catalogPhaseEnteredAt + catalogPhaseMaxWaitMs) {
        throw new Error(timeoutMessage);
      }
      if (afterPoll > catalogStallUntil) {
        throw new Error(stallMessage);
      }
    } else {
      lastCatalogFingerprint = null;
      if (catalogPhaseEnteredAt != null) {
        catalogPhaseEnteredAt = null;
      }
      if (afterPoll >= documentDeadline) {
        throw new Error(timeoutMessage);
      }
    }

    await delay(intervalMs);
  }
}

export async function runCommerceSheetPreviewComplete(
  commerceId: number,
  file: File,
  options?: {
    onUpdate?: (u: SheetPreviewJobPollUpdate) => void;
    signal?: AbortSignal;
    maxWaitMs?: number;
    intervalMs?: number;
    catalogPhaseMaxWaitMs?: number;
    catalogStallMs?: number;
    previewRowsPageSize?: number;
  }
): Promise<SheetPreviewPayload | null> {
  const previewRowsPageSize = options?.previewRowsPageSize ?? SHEET_PREVIEW_JOB_ROWS_PAGE_SIZE;
  let res;
  try {
    res = await postCommerceSheetPreviewAsync(commerceId, file);
  } catch (e: unknown) {
    if (isAxiosError(e) && e.response?.status === 404) {
      return fetchAllSyncPreviewRowPages(commerceId, file, previewRowsPageSize, options?.signal);
    }
    throw e;
  }

  if (res.status === 202) {
    const { jobId } = extractSheetPreviewAsyncMeta(res.data);
    if (!jobId) {
      throw new Error('El servidor aceptó el archivo pero no devolvió jobId.');
    }
    options?.onUpdate?.({ loaderPhase: 'queued', phase: 'queued', detailPhase: null });
    const pipelineRaw = await waitForSheetPreviewJob(commerceId, jobId, options?.onUpdate, {
      signal: options?.signal,
      maxWaitMs: options?.maxWaitMs,
      intervalMs: options?.intervalMs,
      catalogPhaseMaxWaitMs: options?.catalogPhaseMaxWaitMs,
      catalogStallMs: options?.catalogStallMs,
    });
    const merged = await mergePaginatedSheetPreviewJobPages(
      commerceId,
      jobId,
      pipelineRaw,
      previewRowsPageSize,
      options?.signal
    );
    return previewPayloadFromJobOrPipeline(merged);
  }

  return getSheetPreviewPayload(res.data);
}

export type SheetConfirmJobProgress = {
  processed?: number;
  total?: number;
  rowIndex?: number;
  rawName?: string | null;
  saved?: number;
  updated?: number;
  unchanged?: number;
  failed?: number;
};

export type SheetConfirmJobPollUpdate = {
  phase: string;
  progressLabel: string | null;
};

export function sheetConfirmPollModalBody(update: SheetConfirmJobPollUpdate): string {
  const base = 'Estamos publicando tu listado de materiales…';
  const p = (update.phase || '').trim().toLowerCase();
  if (p === 'queued') {
    return `${base}\n\nPreparando la publicación…`;
  }
  const line = update.progressLabel?.trim();
  if (line) {
    return `${base}\n\n${line}`;
  }
  if (p === 'processing') {
    return `${base}\n\nProcesando tus productos…`;
  }
  return base;
}

function parseSheetConfirmJobProgress(raw: unknown): SheetConfirmJobProgress | null {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const p = raw as Record<string, unknown>;
  const num = (k: string): number | undefined => {
    const v = p[k];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string' && v.trim() && Number.isFinite(Number(v))) return Number(v);
    return undefined;
  };
  const rawName =
    typeof p.rawName === 'string'
      ? p.rawName
      : typeof p.raw_name === 'string'
        ? p.raw_name
        : null;
  return {
    processed: num('processed'),
    total: num('total'),
    rowIndex: num('rowIndex') ?? num('row_index'),
    rawName,
    saved: num('saved'),
    updated: num('updated'),
    unchanged: num('unchanged'),
    failed: num('failed'),
  };
}

export function parseSheetConfirmJobPayload(raw: unknown): {
  phase: string;
  errorMessage?: string;
  progress: SheetConfirmJobProgress | null;
  result: unknown;
} {
  const inner = unwrapPreviewData(raw);
  if (!inner) return { phase: 'queued', progress: null, result: undefined };
  const phaseRaw = typeof inner.phase === 'string' && inner.phase.trim().length > 0 ? inner.phase.trim() : 'queued';
  const phase = phaseRaw.toLowerCase();
  const errorMessage =
    typeof inner.errorMessage === 'string' && inner.errorMessage.trim().length > 0
      ? inner.errorMessage.trim()
      : undefined;
  const progress = parseSheetConfirmJobProgress(inner.progress);
  const result = inner.result !== undefined ? inner.result : undefined;
  return { phase, errorMessage, progress, result };
}

function formatSheetConfirmJobProgressLabel(progress: SheetConfirmJobProgress | null): string | null {
  if (!progress) return null;
  const proc = progress.processed;
  const tot = progress.total;
  if (proc != null && tot != null && tot > 0) {
    const name = progress.rawName != null ? String(progress.rawName).trim() : '';
    const tail =
      name.length > 0
        ? ` · ${name.length > 72 ? `${name.slice(0, 72)}…` : name}`
        : '';
    return `Productos ${proc} de ${tot}${tail}`;
  }
  const bits: string[] = [];
  if (progress.saved != null && progress.saved > 0) bits.push(`${progress.saved} nuevos`);
  if (progress.updated != null && progress.updated > 0) bits.push(`${progress.updated} actualizados`);
  if (progress.unchanged != null && progress.unchanged > 0) bits.push(`${progress.unchanged} sin cambios`);
  if (progress.failed != null && progress.failed > 0) bits.push(`${progress.failed} fallidos`);
  return bits.length ? bits.join(' · ') : null;
}

function extractSheetConfirmJobResultEnvelope(jobResponseData: unknown): unknown {
  const st = parseSheetConfirmJobPayload(jobResponseData);
  if (st.result != null) return st.result;
  const inner = unwrapPreviewData(jobResponseData);
  if (inner != null && typeof inner === 'object' && !Array.isArray(inner)) {
    const o = inner as Record<string, unknown>;
    if (Array.isArray(o.results) || o.saved !== undefined || o.updated !== undefined) {
      return inner;
    }
  }
  return jobResponseData;
}

function wrapSheetConfirmResultAsAxiosResponse(resultBody: unknown): AxiosResponse<unknown> {
  return {
    data: { data: resultBody },
    status: 200,
    statusText: 'OK',
    headers: {},
    config: {} as AxiosResponse['config'],
  };
}

async function getCommerceSheetConfirmJob(commerceId: number, jobId: string) {
  return api.get(
    `/material-commerce/commerce/${commerceId}/sheet-confirm-jobs/${encodeURIComponent(jobId)}`,
    getAuthHeaders()
  );
}

async function postCommerceSheetConfirmAsyncRequest(
  commerceId: number,
  rows: unknown[],
  options?: { fromSavedDraft?: boolean }
) {
  const headers = {
    ...getAuthHeaders().headers,
    'Content-Type': 'application/json',
  };
  const url = `/material-commerce/commerce/${commerceId}/sheet-confirm-async`;
  if (options?.fromSavedDraft) {
    return api.post(url, { fromSavedDraft: true }, { headers });
  }
  return api.post(url, { rows }, { headers });
}

async function waitForCommerceSheetConfirmJob(
  commerceId: number,
  jobId: string,
  options?: {
    signal?: AbortSignal;
    maxWaitMs?: number;
    onUpdate?: (u: SheetConfirmJobPollUpdate) => void;
  }
): Promise<unknown> {
  const maxWaitMs = options?.maxWaitMs ?? SHEET_CONFIRM_POLL_MAX_MS;
  const sessionStart = Date.now();
  let intervalMs = SHEET_CONFIRM_POLL_START_MS;
  while (true) {
    if (options?.signal?.aborted) {
      const err = new Error('Aborted') as Error & { name?: string };
      err.name = 'AbortError';
      throw err;
    }
    if (Date.now() - sessionStart > maxWaitMs) {
      throw new Error(
        'La publicación tardó demasiado. Revisá en tu perfil si parte del listado quedó guardada o intentá de nuevo.'
      );
    }
    const response = await getCommerceSheetConfirmJob(commerceId, jobId);
    const st = parseSheetConfirmJobPayload(response.data);
    options?.onUpdate?.({
      phase: st.phase,
      progressLabel: formatSheetConfirmJobProgressLabel(st.progress),
    });
    if (st.phase === 'ready') {
      return response.data;
    }
    if (st.phase === 'failed') {
      throw new Error(st.errorMessage || 'No se pudo publicar el listado.');
    }
    await delay(intervalMs);
    intervalMs = Math.min(SHEET_CONFIRM_POLL_CAP_MS, Math.ceil(intervalMs * 1.35));
  }
}

export async function postCommerceSheetConfirm(
  commerceId: number,
  rows: unknown[],
  options?: {
    fromSavedDraft?: boolean;
    onConfirmPollUpdate?: (u: SheetConfirmJobPollUpdate) => void;
    confirmPollSignal?: AbortSignal;
    confirmPollMaxWaitMs?: number;
  }
): Promise<AxiosResponse<unknown>> {
  const headers = {
    ...getAuthHeaders().headers,
    'Content-Type': 'application/json',
  };
  const postSync = () => {
    if (options?.fromSavedDraft) {
      return api.post(`/material-commerce/commerce/${commerceId}/sheet-confirm`, { fromSavedDraft: true }, {
        headers,
      });
    }
    return api.post(`/material-commerce/commerce/${commerceId}/sheet-confirm`, { rows }, {
      headers,
    });
  };

  let asyncRes: AxiosResponse<unknown>;
  try {
    asyncRes = await postCommerceSheetConfirmAsyncRequest(commerceId, rows, options);
  } catch (e: unknown) {
    if (isAxiosError(e) && e.response?.status === 404) {
      return postSync();
    }
    throw e;
  }

  if (asyncRes.status === 404) {
    return postSync();
  }

  if (asyncRes.status === 202) {
    const { jobId } = extractSheetPreviewAsyncMeta(asyncRes.data);
    if (!jobId) {
      throw new Error('No pudimos iniciar la publicación. Intentá de nuevo en unos minutos.');
    }
    options?.onConfirmPollUpdate?.({ phase: 'queued', progressLabel: null });
    const finishedRaw = await waitForCommerceSheetConfirmJob(commerceId, jobId, {
      signal: options?.confirmPollSignal,
      maxWaitMs: options?.confirmPollMaxWaitMs,
      onUpdate: options?.onConfirmPollUpdate,
    });
    const resultBody = extractSheetConfirmJobResultEnvelope(finishedRaw);
    if (resultBody == null || typeof resultBody !== 'object') {
      throw new Error('No recibimos el resultado de la publicación. Revisá tu perfil por si parte del listado quedó cargado.');
    }
    return wrapSheetConfirmResultAsAxiosResponse(resultBody);
  }

  return asyncRes;
}

export async function postCommerceMaterialItemCreate(commerceId: number, rowOrEnvelope: unknown) {
  let body: unknown;
  if (Array.isArray(rowOrEnvelope)) {
    body = { rows: rowOrEnvelope };
  } else if (
    rowOrEnvelope != null &&
    typeof rowOrEnvelope === 'object' &&
    Array.isArray((rowOrEnvelope as Record<string, unknown>).rows)
  ) {
    body = rowOrEnvelope;
  } else {
    body = rowOrEnvelope;
  }
  return api.post(`/material-commerce/commerce/${commerceId}/items`, body, {
    headers: {
      ...getAuthHeaders().headers,
      'Content-Type': 'application/json',
    },
  });
}

function unwrapSheetDraftEnvelope(raw: unknown): Record<string, unknown> | null {
  if (raw == null || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const d = o.data;
  if (d != null && typeof d === 'object' && !Array.isArray(d)) return d as Record<string, unknown>;
  return o;
}

export type CommerceSheetDraftGet = {
  rows: unknown[];
  updatedAt: string | null;
  rematchByRowIndex: SheetRematchByRowIndex | null;
};

function parseSheetRematchByRowIndexFromUnknown(raw: unknown): SheetRematchByRowIndex | null {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const out: SheetRematchByRowIndex = {};
  for (const [k, v] of Object.entries(o)) {
    const idx = Number(k);
    if (!Number.isFinite(idx) || idx < 0) continue;
    if (v == null || typeof v !== 'object' || Array.isArray(v)) continue;
    const p = v as Record<string, unknown>;
    const uc = p.userCategoryId;
    let userCategoryId: number | '' = '';
    if (typeof uc === 'number' && Number.isFinite(uc) && uc > 0) {
      userCategoryId = uc;
    } else if (typeof uc === 'string' && uc.trim()) {
      const n = Number(uc.trim());
      if (Number.isFinite(n) && n > 0) userCategoryId = n;
    }
    const rawName = typeof p.rawName === 'string' ? p.rawName : '';
    out[idx] = {
      userCategoryId,
      rawName,
      createCatalogMaterial: p.createCatalogMaterial === true,
      rejectSuggestedCatalogMatch: p.rejectSuggestedCatalogMatch === true,
    };
  }
  return Object.keys(out).length > 0 ? out : null;
}

function parseCommerceSheetDraftGet(raw: unknown): CommerceSheetDraftGet | null {
  const inner = unwrapSheetDraftEnvelope(raw);
  if (inner == null) return null;
  const body = inner.body;
  let rows: unknown[] | null = null;
  let updatedAt: string | null = null;
  let rematchByRowIndex: SheetRematchByRowIndex | null = null;
  if (body != null && typeof body === 'object' && !Array.isArray(body)) {
    const b = body as Record<string, unknown>;
    if (Array.isArray(b.rows)) rows = b.rows;
    const ua = b.updatedAt ?? inner.updatedAt;
    if (typeof ua === 'string' && ua.trim()) updatedAt = ua.trim();
    else if (ua != null && (typeof ua === 'number' || typeof ua === 'boolean')) updatedAt = String(ua);
    const rParsed = parseSheetRematchByRowIndexFromUnknown(
      b.rematchByRowIndex != null ? b.rematchByRowIndex : inner.rematchByRowIndex
    );
    if (rParsed) rematchByRowIndex = rParsed;
  }
  if (rows == null && Array.isArray(inner.rows)) rows = inner.rows;
  if (rematchByRowIndex == null && inner.rematchByRowIndex != null) {
    const r = parseSheetRematchByRowIndexFromUnknown(inner.rematchByRowIndex);
    if (r) rematchByRowIndex = r;
  }
  if (rows == null || rows.length === 0) return null;
  return { rows, updatedAt, rematchByRowIndex };
}

export async function getCommerceSheetDraft(commerceId: number): Promise<CommerceSheetDraftGet | null> {
  try {
    const res = await api.get(`/material-commerce/commerce/${commerceId}/sheet-draft`, getAuthHeaders());
    return parseCommerceSheetDraftGet(res.data);
  } catch (e: unknown) {
    if (isAxiosError(e) && (e.response?.status === 404 || e.response?.status === 400)) return null;
    throw e;
  }
}

export async function putCommerceSheetDraft(commerceId: number, rows: unknown[]): Promise<void> {
  await api.put(
    `/material-commerce/commerce/${commerceId}/sheet-draft`,
    { rows },
    {
      headers: {
        ...getAuthHeaders().headers,
        'Content-Type': 'application/json',
      },
    }
  );
}

export async function deleteCommerceSheetDraft(commerceId: number): Promise<void> {
  try {
    await api.delete(`/material-commerce/commerce/${commerceId}/sheet-draft`, getAuthHeaders());
  } catch (e: unknown) {
    if (isAxiosError(e) && e.response?.status === 404) return;
    throw e;
  }
}

export function initRematchMapForPreviewRows(previewRows: unknown[]): SheetRematchByRowIndex {
  const rem: SheetRematchByRowIndex = {};
  previewRows.forEach((row, i) => {
    const rawName = getSheetRowDefaultRawName(row);
    if (rowNeedsCategoryRematch(row)) {
      rem[i] = {
        userCategoryId: '',
        rawName,
        createCatalogMaterial: false,
        rejectSuggestedCatalogMatch: false,
      };
      return;
    }
    if (rowUsesCatalogDirectConfirm(row)) {
      const cid = readSheetRowResolvedUserCategoryId(row);
      rem[i] = {
        userCategoryId: cid ?? '',
        rawName,
        createCatalogMaterial: false,
        rejectSuggestedCatalogMatch: false,
      };
    }
  });
  return rem;
}

export type SheetCatalogMaterialAction = 'none' | 'created' | 'deduped_existing';

export type SheetConfirmResultRow = {
  rowIndex: number;
  materialId: number | null;
  outcome: string;
  reason?: string | null;
  materialCommerceId?: number | null;
  catalogMaterialAction?: SheetCatalogMaterialAction | null;
};

export type SheetConfirmParsed = {
  commerceId: number | null;
  saved: number;
  updated: number;
  unchanged: number;
  failed: unknown[];
  results: SheetConfirmResultRow[];
};

function parseSheetConfirmResultItem(item: unknown): SheetConfirmResultRow | null {
  if (item == null || typeof item !== 'object' || Array.isArray(item)) return null;
  const o = item as Record<string, unknown>;
  const rowIndex = Number(o.rowIndex ?? o.row_index);
  const outcomeRaw = o.outcome ?? o.status;
  const outcome = typeof outcomeRaw === 'string' ? outcomeRaw.trim() : '';
  if (!Number.isFinite(rowIndex) || !outcome) return null;
  const midRaw = o.materialId ?? o.material_id;
  let materialId: number | null = null;
  if (midRaw !== undefined && midRaw !== null && midRaw !== '') {
    const n = Number(midRaw);
    if (Number.isFinite(n)) materialId = n;
  }
  const outcomeLower = outcome.toLowerCase();
  if (materialId == null && outcomeLower !== 'failed') return null;
  const reasonRaw = o.reason ?? o.message;
  const reason = typeof reasonRaw === 'string' && reasonRaw.trim() ? reasonRaw.trim() : null;
  const mcidRaw = o.materialCommerceId ?? o.material_commerce_id;
  let materialCommerceId: number | null = null;
  if (mcidRaw !== undefined && mcidRaw !== null && mcidRaw !== '') {
    const n = Number(mcidRaw);
    if (Number.isFinite(n)) materialCommerceId = n;
  }
  const cmaRaw = o.catalogMaterialAction ?? o.catalog_material_action;
  let catalogMaterialAction: SheetCatalogMaterialAction | null = null;
  if (cmaRaw === 'none' || cmaRaw === 'created' || cmaRaw === 'deduped_existing') {
    catalogMaterialAction = cmaRaw;
  }
  return { rowIndex, materialId, outcome, reason, materialCommerceId, catalogMaterialAction };
}

function selectSheetConfirmRecord(root: Record<string, unknown>): Record<string, unknown> {
  const unwrapOnce = (o: Record<string, unknown>): Record<string, unknown> => {
    const d = o.data;
    return d != null && typeof d === 'object' && !Array.isArray(d) ? (d as Record<string, unknown>) : o;
  };
  let cur = unwrapOnce(root);
  const looksLikeConfirm = (o: Record<string, unknown>) =>
    Array.isArray(o.results) ||
    o.saved !== undefined ||
    o.updated !== undefined ||
    o.unchanged !== undefined ||
    (Array.isArray(o.failed) && o.failed.length > 0);
  if (!looksLikeConfirm(cur)) {
    const d2 = cur.data;
    if (d2 != null && typeof d2 === 'object' && !Array.isArray(d2)) {
      cur = d2 as Record<string, unknown>;
    }
  }
  return cur;
}

export function parseSheetConfirmResponse(apiBody: unknown): SheetConfirmParsed | null {
  if (apiBody == null || typeof apiBody !== 'object') return null;
  const root = apiBody as Record<string, unknown>;
  const inner = selectSheetConfirmRecord(root);
  const resultsRaw = Array.isArray(inner.results) ? inner.results : [];
  const results = resultsRaw.map(parseSheetConfirmResultItem).filter((x): x is SheetConfirmResultRow => x != null);
  let saved = Number(inner.saved);
  let updated = Number(inner.updated);
  let unchanged = Number(inner.unchanged);
  if (!Number.isFinite(saved)) saved = 0;
  if (!Number.isFinite(updated)) updated = 0;
  if (!Number.isFinite(unchanged)) unchanged = 0;
  if (results.length > 0 && saved === 0 && updated === 0 && unchanged === 0) {
    for (const r of results) {
      const o = r.outcome.trim().toLowerCase();
      if (o === 'saved') saved += 1;
      else if (o === 'updated') updated += 1;
      else if (o === 'unchanged') unchanged += 1;
    }
  }
  const commerceIdRaw = inner.commerceId ?? inner.commerce_id;
  const commerceIdNum = Number(commerceIdRaw);
  const commerceId = Number.isFinite(commerceIdNum) ? commerceIdNum : null;
  const failed = Array.isArray(inner.failed) ? inner.failed : [];
  const hasNewShape =
    Array.isArray(inner.results) ||
    inner.saved !== undefined ||
    inner.updated !== undefined ||
    inner.unchanged !== undefined ||
    failed.length > 0;
  if (!hasNewShape) return null;
  return { commerceId, saved, updated, unchanged, failed, results };
}

function buildMaterialIdNameMapFromParsedCategories(cats: ParsedCategory[]): Map<number, string> {
  const m = new Map<number, string>();
  for (const c of cats) {
    for (const mat of c.materials) {
      const id = Number(mat.id);
      if (Number.isFinite(id) && mat.name) m.set(id, mat.name);
    }
  }
  return m;
}

function outcomeLabelEs(outcome: string): string {
  const x = outcome.trim().toLowerCase();
  if (x === 'saved') return 'Nuevo';
  if (x === 'updated') return 'Actualizado';
  if (x === 'unchanged') return 'Sin cambios';
  if (x === 'failed') return 'Falló';
  return outcome;
}

function userFacingSheetConfirmFailureReason(raw: string | null | undefined): string {
  const s = typeof raw === 'string' ? raw.trim() : '';
  if (!s) return 'No se pudo guardar este producto.';
  const low = s.toLowerCase();
  if (low.includes('sin materialid resuelto')) {
    return 'No se pudo guardar; revisá categoría, nombre y unidad en ese producto y volvé a publicar.';
  }
  return s;
}

function buildSheetConfirmOutcomeSummaryPhrase(
  saved: number,
  updated: number,
  unchanged: number,
  failedCount: number
): string {
  const chunks: string[] = [];
  if (saved > 0) {
    chunks.push(saved === 1 ? 'se publicó 1 producto nuevo' : `se publicaron ${saved} productos nuevos`);
  }
  if (updated > 0) {
    chunks.push(updated === 1 ? 'se actualizó 1 oferta' : `se actualizaron ${updated} ofertas`);
  }
  if (unchanged > 0) {
    chunks.push(
      unchanged === 1
        ? '1 producto ya estaba cargado igual (sin cambios)'
        : `${unchanged} productos ya estaban cargados igual (sin cambios)`
    );
  }
  if (chunks.length === 0) {
    if (failedCount > 0) return `No pudimos publicar ${failedCount} producto(s).`;
    return 'Tu listado quedó publicado.';
  }
  const cap = chunks.join(', ');
  const head = cap.charAt(0).toUpperCase() + cap.slice(1);
  if (failedCount > 0) return `${head}. No pudimos publicar ${failedCount} producto(s).`;
  return `${head}.`;
}

function summarizeFailed(failed: unknown[]): string | null {
  if (failed.length === 0) return null;
  const bits = failed.slice(0, 5).map((f) => {
    if (f != null && typeof f === 'object' && !Array.isArray(f)) {
      const o = f as Record<string, unknown>;
      const ri = o.rowIndex;
      const msg = o.message ?? o.error ?? o.reason;
      if (typeof msg === 'string' && msg.trim()) {
        return `Producto ${ri != null ? String(ri) : '?'}: ${userFacingSheetConfirmFailureReason(msg.trim())}`;
      }
    }
    try {
      return JSON.stringify(f);
    } catch {
      return String(f);
    }
  });
  const more = failed.length > 5 ? ` (y ${failed.length - 5} más)` : '';
  return bits.join(' · ') + more;
}

export async function loadSheetConfirmSummary(
  commerceId: number,
  confirmApiBody: unknown
): Promise<{ headline: string; lines: string[]; failedNote: string | null }> {
  const parsed = parseSheetConfirmResponse(confirmApiBody);
  let nameMap = new Map<number, string>();
  try {
    const cats = await fetchCommerceCatalogMaterialsParsed(commerceId);
    nameMap = buildMaterialIdNameMapFromParsedCategories(cats);
  } catch {
    nameMap = new Map();
  }

  if (!parsed) {
    return {
      headline: 'El listado se guardó correctamente.',
      lines: [],
      failedNote: null,
    };
  }

  const lines = [...parsed.results]
    .sort((a, b) => a.rowIndex - b.rowIndex)
    .map((r) => {
      const oc = r.outcome.trim().toLowerCase();
      if (oc === 'failed') {
        const reason = r.reason ? ` — ${userFacingSheetConfirmFailureReason(r.reason)}` : '';
        const midPart =
          r.materialId != null ? (nameMap.get(r.materialId) ?? `Material ${r.materialId}`) : 'Material sin identificar';
        return `Producto ${r.rowIndex} — ${midPart} — ${outcomeLabelEs(r.outcome)}${reason}`;
      }
      const nm =
        r.materialId != null ? (nameMap.get(r.materialId) ?? `Material ${r.materialId}`) : 'Material';
      return `Producto ${r.rowIndex} — ${nm} — ${outcomeLabelEs(r.outcome)}`;
    });

  const headline = buildSheetConfirmOutcomeSummaryPhrase(
    parsed.saved,
    parsed.updated,
    parsed.unchanged,
    parsed.failed.length
  );
  const failedDetail = summarizeFailed(parsed.failed);
  const failedNote = failedDetail
    ? `No se publicaron ${parsed.failed.length} producto(s): ${failedDetail}`
    : null;

  return { headline, lines, failedNote };
}

export type SheetConfirmSummaryUi = Awaited<ReturnType<typeof loadSheetConfirmSummary>>;

export type SheetConfirmSubmitResult =
  | { ok: true; summary: SheetConfirmSummaryUi }
  | { ok: false; message: string };

export const SHEET_CONFIRM_SUMMARY_DETAIL_CAP = 6;

export function sheetConfirmSummaryLinesForDisplay(
  lines: string[],
  cap = SHEET_CONFIRM_SUMMARY_DETAIL_CAP
): { shown: string[]; rest: number } {
  if (cap <= 0) return { shown: [], rest: lines.length };
  const shown = lines.slice(0, cap);
  return { shown, rest: lines.length - shown.length };
}

export function formatSheetConfirmSummaryAsPlainText(
  summary: {
    headline: string;
    lines: string[];
    failedNote: string | null;
  },
  options?: { maxDetailLines?: number }
): string {
  const cap =
    options?.maxDetailLines != null && options.maxDetailLines >= 0
      ? options.maxDetailLines
      : SHEET_CONFIRM_SUMMARY_DETAIL_CAP;
  const parts: string[] = [summary.headline];
  if (summary.failedNote) parts.push(summary.failedNote);
  const footer =
    'Podés revisar el listado completo en tu perfil, pestaña Gestión → Mi comercio.';
  if (summary.lines.length > 0) {
    parts.push('');
    const shown = cap === 0 ? [] : summary.lines.slice(0, cap);
    for (const line of shown) {
      parts.push(`• ${line}`);
    }
    const rest = summary.lines.length - shown.length;
    if (rest > 0) {
      parts.push('', `…y ${rest} producto(s) más.`, '', footer);
    } else {
      parts.push('', footer);
    }
  } else {
    parts.push('', footer);
  }
  return parts.join('\n');
}

export function extractCommerceIdFromCreateResponse(res: { data?: unknown }): number | null {
  const raw = res.data;
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const inner = (o.data && typeof o.data === 'object' ? o.data : o) as Record<string, unknown>;
  const id = inner.id ?? inner.commerceId;
  if (id == null || id === '') return null;
  const n = Number(id);
  return Number.isFinite(n) ? n : null;
}

export function extractRowsFromSheetPreviewResponse(resBody: unknown): unknown[] {
  return getSheetPreviewPayload(resBody)?.rows ?? [];
}

export function isAcceptedPriceListFile(file: File): boolean {
  const t = (file.type || '').trim().toLowerCase();
  const n = (file.name || '').trim().toLowerCase();
  if (n.endsWith('.pdf') || t === 'application/pdf') return true;
  if (/\.(jpe?g|png|webp)$/.test(n) || ['image/jpeg', 'image/png', 'image/webp'].includes(t)) return true;
  if (n.endsWith('.txt') || t === 'text/plain') return true;
  if (n.endsWith('.json') || t === 'application/json') return true;
  if (n.endsWith('.csv') || t === 'text/csv' || t === 'application/csv') return true;
  if (
    n.endsWith('.xlsx') ||
    n.endsWith('.xls') ||
    t === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    t === 'application/vnd.ms-excel'
  ) {
    return true;
  }
  if (t === 'application/octet-stream' || t === '') {
    return /\.(pdf|jpe?g|png|webp|txt|json|csv|xlsx|xls)$/i.test(n);
  }
  return false;
}
